/* =========================
   PEGUU — CART DRAWER
   CARRITO HÍBRIDO

   Invitado:
   localStorage

   Autenticado:
   API Peguu One
========================= */

(() => {
  "use strict";


  /* =========================
     CONFIGURACIÓN
  ========================= */

  const API_BASE_URL =
    window.PeguuApi.endpoint("customers");

  const AUTH_STORAGE_KEY =
    "peguu:customer-auth";

  const GUEST_CART_STORAGE_KEY =
    "peguu:guest-cart";

  const CART_EDIT_STORAGE_KEY =
    "peguu:cart-edit";

  const MATERIAL_STORAGE_KEY =
    "peguu:selected-material";

  const ORDER_STORAGE_KEY =
    "peguu:sticker-order";

  const DESIGN_STORAGE_KEY =
    "peguu:sticker-design";

  const QR_CONFIG_STORAGE_KEY =
    "peguu:qr-config";

  const GUEST_COUPON_STORAGE_KEY =
    "peguu:guest-coupon";

  const AUTOMATIC_COUPON_CODE =
    "CLIENTEPEGUU";

  const MANUAL_CUSTOMER_COUPONS_STORAGE_KEY =
    "peguu:manual-customer-coupons";

  const PAYMENT_STORAGE_KEY =
    "peguu:sticker-payment";

  const CUSTOMER_CART_METADATA_KEY =
    "peguu:customer-cart-metadata";

  const DESIGN_FILE_METADATA_KEY =
    "peguu:design-file-metadata";

  const DESIGN_FILE_DB =
    "peguu-design-files";

  const DESIGN_FILE_STORE =
    "files";

  const CART_OPEN_CACHE_TTL_MS =
    30000;

  const activeLocalPreviewUrls =
    new Set();

  const UPLOADED_DESIGN_PLACEHOLDER =
    `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="172" height="172" viewBox="0 0 172 172">
        <rect width="172" height="172" rx="24" fill="#f1ecfa"/>
        <path d="M55 31h42l25 25v73c0 7-6 12-13 12H55c-7 0-13-5-13-12V43c0-7 6-12 13-12Z" fill="#fff" stroke="#835ec7" stroke-width="6"/>
        <path d="M97 31v25h25" fill="none" stroke="#835ec7" stroke-width="6" stroke-linejoin="round"/>
        <path d="m61 92 14 14 31-34" fill="none" stroke="#6ac14c" stroke-width="9" stroke-linecap="round" stroke-linejoin="round"/>
        <text x="86" y="157" text-anchor="middle" font-family="Arial,sans-serif" font-size="15" font-weight="700" fill="#574676">Archivo cargado</text>
      </svg>
    `)}`;

  let guestQuoteCache = {
    signature: "",
    result: null,
    promise: null,
  };

  let availableCouponsCache = {
    signature: "",
    result: [],
    promise: null,
  };


  /* =========================
     UTILIDADES
  ========================= */

  const parseStorageJson = (
    storage,
    key,
    fallbackValue = null
  ) => {
    try {
      const storedValue =
        storage.getItem(key);

      if (!storedValue) {
        return fallbackValue;
      }

      return JSON.parse(storedValue);
    } catch (error) {
      console.warn(
        `Peguu Cart: no se pudo leer ${key}.`,
        error
      );

      return fallbackValue;
    }
  };


  const normalizeText = (value) => {
    return String(
      value === null ||
      value === undefined
        ? ""
        : value
    ).trim();
  };


  const normalizeNumber = (
    value,
    fallbackValue = 0
  ) => {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return fallbackValue;
    }

    if (typeof value === "number") {
      return Number.isFinite(value)
        ? value
        : fallbackValue;
    }

    const normalizedValue =
      String(value)
        .trim()
        .replace(/\s/g, "")
        .replace(/,/g, "")
        .replace(/[^\d.-]/g, "");

    const numericValue =
      Number(normalizedValue);

    return Number.isFinite(numericValue)
      ? numericValue
      : fallbackValue;
  };

  const getVisualDesignUrl = (item) => {
    const design = item?.design;

    if (!design || design.uploadLater === true) {
      return "";
    }

    const previewUrl = normalizeText(
      design.thumbnailUrl ||
      design.previewUrl ||
      design.thumbnail_url ||
      design.preview_url ||
      design.thumbnail?.url ||
      design.preview?.url
    );

    if (previewUrl) {
      return previewUrl;
    }

    const visualUrl = normalizeText(
      design.fileUrl ||
      design.signedUrl ||
      design.downloadUrl ||
      design.publicUrl ||
      design.storageUrl ||
      design.imageUrl ||
      design.assetUrl ||
      design.secureUrl ||
      design.src ||
      design.file_url ||
      design.signed_url ||
      design.download_url ||
      design.public_url ||
      design.storage_url ||
      design.image_url ||
      design.asset_url ||
      design.secure_url ||
      design.url ||
      design.file?.url
    );

    if (!visualUrl) {
      return normalizeText(
        design.fileId || design.file_id
      )
        ? UPLOADED_DESIGN_PLACEHOLDER
        : "";
    }

    const extension = normalizeText(
      design.extension ||
      design.fileName?.split(".").pop()
    ).toLowerCase();
    const fileType = normalizeText(
      design.fileType ||
      design.mimeType ||
      design.contentType
    ).toLowerCase();

    if (
      extension &&
      !["jpg", "jpeg", "png", "webp", "gif", "svg"]
        .includes(extension) &&
      !fileType.startsWith("image/")
    ) {
      return "";
    }

    return visualUrl;
  };

  const getCartItemId = (item) => {
    return normalizeText(
      item?.id ||
      item?._id ||
      item?.itemId ||
      item?.cartItemId
    );
  };

  const getApiItemDesign = (item) => {
    const apiDesign =
      item?.design ||
      item?.file ||
      item?.designFile ||
      item?.primaryFile ||
      null;

    const previewUrl = normalizeText(
      apiDesign?.thumbnailUrl ||
      apiDesign?.thumbnail_url ||
      apiDesign?.previewUrl ||
      apiDesign?.preview_url ||
      apiDesign?.thumbnail?.url ||
      apiDesign?.preview?.url ||
      item?.thumbnailUrl ||
      item?.thumbnail_url ||
      item?.previewUrl ||
      item?.preview_url ||
      item?.thumbnail?.url ||
      item?.preview?.url
    );

    const fileUrl = normalizeText(
      apiDesign?.fileUrl ||
      apiDesign?.file_url ||
      apiDesign?.signedUrl ||
      apiDesign?.signed_url ||
      apiDesign?.downloadUrl ||
      apiDesign?.download_url ||
      apiDesign?.publicUrl ||
      apiDesign?.public_url ||
      apiDesign?.storageUrl ||
      apiDesign?.storage_url ||
      apiDesign?.imageUrl ||
      apiDesign?.image_url ||
      apiDesign?.assetUrl ||
      apiDesign?.asset_url ||
      apiDesign?.secureUrl ||
      apiDesign?.secure_url ||
      apiDesign?.src ||
      apiDesign?.url ||
      apiDesign?.file?.url ||
      item?.fileUrl ||
      item?.file_url ||
      item?.designUrl ||
      item?.design_url ||
      item?.signedUrl ||
      item?.signed_url ||
      item?.downloadUrl ||
      item?.download_url ||
      item?.publicUrl ||
      item?.public_url ||
      item?.storageUrl ||
      item?.storage_url ||
      item?.imageUrl ||
      item?.image_url ||
      item?.assetUrl ||
      item?.asset_url ||
      item?.secureUrl ||
      item?.secure_url ||
      item?.src
    );

    const fileId = normalizeText(
      apiDesign?.fileId ||
      apiDesign?.file_id ||
      apiDesign?.id ||
      item?.fileId ||
      item?.file_id ||
      item?.designFileId
    );

    if (!apiDesign && !fileId && !previewUrl && !fileUrl) {
      return null;
    }

    return {
      ...(apiDesign || {}),
      fileId: fileId || null,
      fileUrl: fileUrl || null,
      fileName:
        apiDesign?.fileName ||
        apiDesign?.file_name ||
        apiDesign?.originalName ||
        apiDesign?.original_name ||
        apiDesign?.name ||
        item?.fileName ||
        item?.file_name ||
        item?.originalName ||
        item?.original_name ||
        null,
      previewUrl: previewUrl || fileUrl || null,
      fileType:
        apiDesign?.fileType ||
        apiDesign?.mimeType ||
        apiDesign?.contentType ||
        item?.fileType ||
        item?.mimeType ||
        item?.contentType ||
        null,
      extension:
        apiDesign?.extension ||
        item?.extension ||
        normalizeText(
          apiDesign?.fileName ||
          apiDesign?.originalName ||
          item?.fileName
        ).split(".").pop()
    };
  };

  const getLocalDesignPreviewKey = (
    item
  ) => {
    const design = item?.design;
    const extension = normalizeText(
      design?.extension ||
      design?.fileName?.split(".").pop()
    ).toLowerCase();

    if (
      design?.uploadLater === true ||
      !["jpg", "jpeg", "png", "webp"]
        .includes(extension)
    ) {
      return "";
    }

    return normalizeText(
      design?.localPreviewKey
    );
  };

  const getLocalDesignFile = (key) => {
    if (!key) {
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      const request = indexedDB.open(
        DESIGN_FILE_DB,
        1
      );

      request.onupgradeneeded = () => {
        if (
          !request.result.objectStoreNames
            .contains(DESIGN_FILE_STORE)
        ) {
          request.result.createObjectStore(
            DESIGN_FILE_STORE
          );
        }
      };

      request.onerror = () => resolve(null);
      request.onsuccess = () => {
        const database = request.result;
        const transaction = database.transaction(
          DESIGN_FILE_STORE,
          "readonly"
        );
        const getRequest = transaction
          .objectStore(DESIGN_FILE_STORE)
          .get(key);

        getRequest.onsuccess = () => {
          resolve(getRequest.result || null);
          database.close();
        };
        getRequest.onerror = () => {
          resolve(null);
          database.close();
        };
      };
    });
  };

  const hydrateLocalDesignPreviews = async (
    container
  ) => {
    activeLocalPreviewUrls.forEach(
      (url) => URL.revokeObjectURL(url)
    );
    activeLocalPreviewUrls.clear();

    const images = container.querySelectorAll(
      ".peguu-cart-drawer__product-image"
    );

    images.forEach((image) => {
      const fallback = normalizeText(
        image.dataset.materialImageFallback
      );

      if (!fallback) {
        return;
      }

      image.addEventListener(
        "error",
        () => {
          image.src = fallback;
        },
        { once: true }
      );
    });

    await Promise.all(
      Array.from(images).map(async (image) => {
        if (
          !image.dataset.localDesignPreview
        ) {
          return;
        }

        const file = await getLocalDesignFile(
          image.dataset.localDesignPreview
        );

        if (!file) {
          return;
        }

        const url = URL.createObjectURL(file);
        activeLocalPreviewUrls.add(url);
        image.src = url;
      })
    );
  };


  const escapeHtml = (value) => {
    const element =
      document.createElement("div");

    element.textContent =
      value === null ||
      value === undefined
        ? ""
        : String(value);

    return element.innerHTML;
  };


  const formatMoney = (
    amount,
    currency = "MXN"
  ) => {
    const value =
      normalizeNumber(amount, 0);

    try {
      return new Intl.NumberFormat(
        "es-MX",
        {
          style: "currency",
          currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }
      ).format(value);
    } catch (error) {
      return `$${value.toLocaleString(
        "es-MX",
        {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }
      )}`;
    }
  };

  const createLocalItemId = () => {
    if (
      window.crypto &&
      typeof window.crypto.randomUUID ===
        "function"
    ) {
      return window.crypto.randomUUID();
    }

    return (
      `guest-${Date.now()}-` +
      Math.random()
        .toString(36)
        .slice(2, 10)
    );
  };


  /* =========================
     SESIÓN DEL CLIENTE
  ========================= */

  const readCustomerSession = () => {
    if (window.PeguuAuthClient?.getSession) {
      return window.PeguuAuthClient.getSession();
    }

    if (
      window.PeguuAuth &&
      typeof window.PeguuAuth.getSession ===
        "function"
    ) {
      return (
        window.PeguuAuth.getSession() ||
        null
      );
    }

    return parseStorageJson(
      window.localStorage,
      AUTH_STORAGE_KEY,
      null
    );
  };


  const getCustomerToken = () => {
    const session =
      readCustomerSession();

    return normalizeText(
      session?.token
    );
  };


  const getCustomerCouponScope = (
    cart = null
  ) => {
    const session =
      readCustomerSession();

    return normalizeText(
      cart?.customerId ||
      session?.customer?.id ||
      session?.customer?.customerId ||
      session?.customer?.email
    ).toLowerCase();
  };


  const readManualCustomerCoupons = (
    cart = null
  ) => {
    const stored = parseStorageJson(
      window.sessionStorage,
      MANUAL_CUSTOMER_COUPONS_STORAGE_KEY,
      null
    );
    const scope =
      getCustomerCouponScope(cart);
    const cartId =
      normalizeText(cart?.id);
    const storedScope =
      normalizeText(stored?.scope).toLowerCase();
    const storedCartId =
      normalizeText(stored?.cartId);
    const sameOwner =
      Boolean(scope) && storedScope === scope;
    const sameCart =
      Boolean(cartId) && storedCartId === cartId;

    if (!sameOwner && !sameCart) {
      return [];
    }

    return [...new Set(
      (Array.isArray(stored?.codes) ? stored.codes : [])
        .map((code) => normalizeText(code).toUpperCase())
        .filter(Boolean)
    )];
  };


  const saveManualCustomerCoupons = (
    codes,
    cart = null
  ) => {
    const normalizedCodes = [...new Set(
      (Array.isArray(codes) ? codes : [codes])
        .map((code) => normalizeText(code).toUpperCase())
        .filter((code) => code && code !== AUTOMATIC_COUPON_CODE)
    )].slice(0, 1);

    try {
      if (!normalizedCodes.length) {
        window.sessionStorage.removeItem(
          MANUAL_CUSTOMER_COUPONS_STORAGE_KEY
        );
        return;
      }

      window.sessionStorage.setItem(
        MANUAL_CUSTOMER_COUPONS_STORAGE_KEY,
        JSON.stringify({
          scope:
            getCustomerCouponScope(cart),
          cartId:
            normalizeText(cart?.id),
          codes:
            normalizedCodes,
        })
      );
    } catch (error) {
      console.warn(
        "Peguu Cart: no se pudo conservar el origen manual del cupón.",
        error
      );
    }
  };


  const rememberManualCustomerCoupon = (
    code,
    cart = null
  ) => {
    saveManualCustomerCoupons([code], cart);
  };


  const forgetManualCustomerCoupon = (
    code,
    cart = null
  ) => {
    const normalizedCode =
      normalizeText(code).toUpperCase();

    saveManualCustomerCoupons(
      readManualCustomerCoupons(cart).filter(
        (storedCode) => storedCode !== normalizedCode
      ),
      cart
    );
  };


  /* =========================
     RESPUESTAS DE API
  ========================= */

  const parseApiResponse = async (
    response,
    fallbackMessage
  ) => {
    let payload = null;

    try {
      payload =
        await response.json();
    } catch (error) {
      payload = null;
    }

    if (
      !response.ok ||
      !payload?.success
    ) {
      const apiError =
        new Error(
          payload?.message ||
          fallbackMessage ||
          "No fue posible completar la solicitud."
        );

      apiError.status =
        response.status;

      apiError.code =
        payload?.code ||
        null;

      throw apiError;
    }

    return payload;
  };


  /* =========================
     CARRITO DE INVITADO
  ========================= */

  const readGuestCart = () => {
    const storedCart =
      parseStorageJson(
        window.localStorage,
        GUEST_CART_STORAGE_KEY,
        []
      );

    return Array.isArray(storedCart)
      ? storedCart
      : [];
  };


  const saveGuestCart = (items) => {
    window.localStorage.setItem(
      GUEST_CART_STORAGE_KEY,
      JSON.stringify(items)
    );

    guestQuoteCache = {
      signature: "",
      result: null,
      promise: null,
    };

    availableCouponsCache = {
      signature: "",
      result: [],
      promise: null,
    };
  };


  const writeSessionJson = (
    key,
    value
  ) => {
    window.sessionStorage.setItem(
      key,
      JSON.stringify(value)
    );
  };


  const createEditDesignState = (
    item
  ) => {
    const design =
      item?.design || {};
    const fileId =
      normalizeText(
        item?.fileId ||
        design?.fileId
      ) || null;
    const fileName =
      normalizeText(
        design?.fileName ||
        design?.originalName
      ) ||
      (fileId
        ? "Diseño guardado"
        : "");

    if (
      !fileId &&
      !fileName &&
      design?.uploadLater !== true
    ) {
      return {
        uploadLater: true,
        primaryFile: null,
      };
    }

    return {
      uploadLater:
        design?.uploadLater === true &&
        !fileId,
      primaryFile:
        fileId || fileName
          ? {
              fileId,
              fileName,
              fileType:
                normalizeText(
                  design?.fileType ||
                  design?.mimeType
                ),
              fileSize:
                normalizeNumber(
                  design?.fileSize ||
                  design?.size,
                  0
                ),
              extension:
                normalizeText(
                  design?.extension
                ),
              fileUrl:
                normalizeText(
                  design?.fileUrl ||
                  design?.previewUrl ||
                  design?.url
                ) || null,
              localPreviewKey:
                normalizeText(
                  design?.localPreviewKey
                ) || null,
              uploadStatus:
                fileId
                  ? "uploaded"
                  : "local",
            }
          : null,
    };
  };


  const beginCartItemEdit = (
    item,
    returnUrl = "/pages/arma-tus-stickers-resumen"
  ) => {
    if (
      !item ||
      item.productCode === "sample_pack"
    ) {
      throw new Error(
        "Este producto no tiene opciones editables."
      );
    }

    const itemId =
      normalizeText(item.id);

    if (!itemId) {
      throw new Error(
        "No encontramos el identificador del producto."
      );
    }

    const material = {
      id:
        normalizeText(item.materialId),
      name:
        normalizeText(item.materialName),
      slug:
        normalizeText(item.materialSlug),
      productCode:
        normalizeText(item.productCode),
      imageUrl:
        normalizeText(item.materialImageUrl),
    };
    const designState =
      createEditDesignState(item);
    const isStandardQuantity =
      [25, 50, 100, 250, 500, 1000, 2000]
        .includes(Number(item.quantity));
    const order = {
      material,
      measurements: {
        widthCm:
          normalizeNumber(item.widthCm, 0),
        heightCm:
          normalizeNumber(item.heightCm, 0),
      },
      quote: {
        widthCm:
          normalizeNumber(item.widthCm, 0),
        heightCm:
          normalizeNumber(item.heightCm, 0),
        quantity:
          Math.max(
            1,
            Math.trunc(
              normalizeNumber(
                item.quantity,
                1
              )
            )
          ),
        unitPrice:
          normalizeNumber(item.unitPrice, 0),
        total:
          normalizeNumber(
            item.lineTotal ||
            item.subtotal,
            0
          ),
        selectionType:
          isStandardQuantity
            ? "standard"
            : "custom",
      },
      shape:
        normalizeText(item.shape),
      design:
        designState.primaryFile
          ? {
              ...designState.primaryFile,
              uploadLater: false,
            }
          : {
              uploadLater: true,
            },
    };
    const qrConfig =
      item.qrConfig || {};
    const editState = {
      mode: "edit",
      cartType:
        item.source === "customer"
          ? "authenticated"
          : "guest",
      itemId,
      returnUrl,
      originalFileId:
        normalizeText(item.fileId) ||
        null,
      originalFileId2:
        normalizeText(item.fileId2) ||
        null,
      productCode:
        material.productCode,
    };

    writeSessionJson(
      CART_EDIT_STORAGE_KEY,
      editState
    );
    writeSessionJson(
      MATERIAL_STORAGE_KEY,
      material
    );
    writeSessionJson(
      ORDER_STORAGE_KEY,
      order
    );
    writeSessionJson(
      DESIGN_STORAGE_KEY,
      designState
    );

    if (
      material.productCode ===
      "qr_sticker"
    ) {
      writeSessionJson(
        QR_CONFIG_STORAGE_KEY,
        {
          materialId:
            material.id,
          materialName:
            material.name,
          productCode:
            material.productCode,
          destinationUrl:
            normalizeText(
              qrConfig.link ||
              qrConfig.destinationUrl ||
              qrConfig.destination_url ||
              qrConfig.url
            ),
          acabado:
            normalizeText(
              qrConfig.acabado ||
              qrConfig.finish
            ),
          posicion:
            normalizeText(
              qrConfig.posicion ||
              qrConfig.position
            ),
        }
      );
    } else {
      window.sessionStorage.removeItem(
        QR_CONFIG_STORAGE_KEY
      );
    }

    window.location.assign(
      material.productCode ===
        "qr_sticker"
        ? "/pages/configura-qr"
        : "/pages/arma-tus-stickers"
    );
  };


  const readGuestCouponCodes = () => {
    try {
      const storedValue =
        window.localStorage.getItem(
          GUEST_COUPON_STORAGE_KEY
        );

      if (!storedValue) {
        return [];
      }

      let values;

      try {
        const parsed =
          JSON.parse(storedValue);

        values = Array.isArray(parsed)
          ? parsed
          : [parsed];
      } catch (error) {
        values = [storedValue];
      }

      return [...new Set(values
        .map((code) => normalizeText(code).toUpperCase())
        .filter((code) => code && code !== AUTOMATIC_COUPON_CODE))]
        .slice(0, 1);
    } catch (error) {
      return [];
    }
  };


  const readGuestCouponCode = () =>
    readGuestCouponCodes()[0] || "";


  const getCartCouponCodes = (
    cart
  ) => {
    const codes = [];

    if (
      Array.isArray(cart?.coupons)
    ) {
      cart.coupons.forEach(
        (coupon) => {
          const code =
            normalizeText(
              coupon?.code
            ).toUpperCase();

          if (code) {
            codes.push(code);
          }
        }
      );
    }

    const singleCode =
      normalizeText(
        cart?.coupon?.code
      ).toUpperCase();

    if (singleCode) {
      codes.push(singleCode);
    }

    return [
      ...new Set(
        codes
      ),
    ];
  };


  const getPromotionalCouponCodes = (
    cart
  ) => getCartCouponCodes(cart).filter(
    (code) => code !== AUTOMATIC_COUPON_CODE
  ).slice(0, 1);


  const quotedCouponApplies = (
    quoted,
    sentCode
  ) => {
    const normalizedCode =
      normalizeText(sentCode)
        .toUpperCase();

    if (!normalizedCode) {
      return true;
    }

    return getCartCouponCodes(
      quoted
    ).includes(
      normalizedCode
    );
  };


  const saveGuestCouponCode = (
    code
  ) => {
    const normalizedCode =
      normalizeText(code)
        .toUpperCase();

    const codes = normalizedCode &&
      normalizedCode !== AUTOMATIC_COUPON_CODE
        ? [normalizedCode]
        : [];

    if (!codes.length) {
      window.localStorage.removeItem(
        GUEST_COUPON_STORAGE_KEY
      );
    } else {
      window.localStorage.setItem(
        GUEST_COUPON_STORAGE_KEY,
        JSON.stringify(codes)
      );
    }

    guestQuoteCache = {
      signature: "",
      result: null,
      promise: null,
    };

    availableCouponsCache = {
      signature: "",
      result: [],
      promise: null,
    };
  };


  const removeGuestCouponCode = (
    code
  ) => {
    const normalizedCode =
      normalizeText(code).toUpperCase();
    const codes = readGuestCouponCodes()
      .filter((item) => item !== normalizedCode);

    if (codes.length) {
      window.localStorage.setItem(
        GUEST_COUPON_STORAGE_KEY,
        JSON.stringify(codes)
      );
    } else {
      window.localStorage.removeItem(
        GUEST_COUPON_STORAGE_KEY
      );
    }

    guestQuoteCache = {
      signature: "",
      result: null,
      promise: null,
    };

    availableCouponsCache = {
      signature: "",
      result: [],
      promise: null,
    };
  };


  const clearGuestCouponCode = () => {
    window.localStorage.removeItem(
      GUEST_COUPON_STORAGE_KEY
    );

    guestQuoteCache = {
      signature: "",
      result: null,
      promise: null,
    };

    availableCouponsCache = {
      signature: "",
      result: [],
      promise: null,
    };
  };


  const getGuestEmail = () => {
    const session =
      readCustomerSession();

    const sessionEmail =
      normalizeText(
        session?.customer?.email
      );

    if (sessionEmail) {
      return sessionEmail;
    }

    try {
      const shippingData =
        JSON.parse(
          window.sessionStorage.getItem(
            "peguu:shipping-data"
          ) || "null"
        );

      return normalizeText(
        shippingData?.email
      );
    } catch (error) {
      return "";
    }
  };


  const normalizeCoupon = (
    coupon
  ) => {
    if (
      !coupon ||
      typeof coupon !== "object"
    ) {
      return null;
    }

    const code =
      normalizeText(coupon.code)
        .toUpperCase();

    if (!code) {
      return null;
    }

    return {
      id:
        normalizeText(coupon.id),

      code,

      name:
        normalizeText(coupon.name),

      description:
        coupon.description == null
          ? null
          : normalizeText(
              coupon.description
            ) || null,

      discountPercent:
        normalizeNumber(
          coupon.discountPercent,
          0
        ),

      discountAmount:
        normalizeNumber(
          coupon.discountAmount,
          0
        ),

      endsAt:
        coupon.endsAt || null,

      minSubtotal:
        coupon.minSubtotal == null ||
        coupon.minSubtotal === ""
          ? null
          : normalizeNumber(
              coupon.minSubtotal,
              0
            ),

      firstOrderOnly:
        coupon.firstOrderOnly ===
        true,

      allowGuests:
        coupon.allowGuests !==
        false,

      appliesToProductCodes:
        Array.isArray(
          coupon.appliesToProductCodes
        )
          ? coupon.appliesToProductCodes
          : [],
    };
  };


  const normalizeCouponError = (
    value
  ) => {
    const message =
      normalizeText(value);

    return message || null;
  };


  const normalizePricing = (
    pricing,
    fallback = {}
  ) => {
    const source =
      pricing &&
      typeof pricing === "object"
        ? pricing
        : {};

    return {
      subtotal:
        normalizeNumber(
          source.subtotal,
          fallback.subtotal || 0
        ),

      discountAmount:
        normalizeNumber(
          source.discountAmount,
          fallback.discountAmount || 0
        ),

      shippingCost:
        normalizeNumber(
          source.shippingCost,
          fallback.shippingCost || 0
        ),

      freeShipping:
        source.freeShipping === true,

      freeShippingThreshold:
        normalizeNumber(
          source.freeShippingThreshold,
          fallback.freeShippingThreshold ??
            null
        ),

      total:
        normalizeNumber(
          source.total,
          fallback.total || 0
        ),
    };
  };


  const buildGuestQuoteItem = (
    item
  ) => {
    const normalized =
      normalizeGuestItem(item);

    const payload = {
      id:
        normalized.id,

      materialId:
        normalized.materialId,
    };

    if (
      normalized.productCode ===
      "sample_pack"
    ) {
      return payload;
    }

    payload.widthCm =
      normalized.widthCm;

    payload.heightCm =
      normalized.heightCm;

    payload.quantity =
      normalized.quantity;

    if (normalized.fileId) {
      payload.fileId =
        normalized.fileId;
    }

    if (normalized.fileId2) {
      payload.fileId2 =
        normalized.fileId2;
    }

    if (normalized.qrConfig) {
      payload.qrConfig =
        normalized.qrConfig;
    }

    return payload;
  };


  const mergeGuestQuotedItems = (
    localItems,
    quotedItems
  ) => {
    const quotedById =
      new Map(
        (Array.isArray(quotedItems)
          ? quotedItems
          : []
        ).map((item) => [
          normalizeText(item?.id),
          item,
        ])
      );

    return localItems.map(
      (localItem) => {
        const quoted =
          quotedById.get(
            localItem.id
          );

        if (!quoted) {
          return localItem;
        }

        return {
          ...localItem,

          unitPrice:
            normalizeNumber(
              quoted.unitPrice,
              localItem.unitPrice
            ),

          lineTotal:
            normalizeNumber(
              quoted.lineTotal,
              localItem.lineTotal
            ),

          priceValid:
            quoted.priceValid !==
            false,

          materialName:
            localItem.materialName ||
            normalizeText(
              quoted.materialName
            ),

          materialImageUrl:
            localItem.materialImageUrl ||
            normalizeText(
              quoted.materialImageUrl
            ),

          productCode:
            localItem.productCode ||
            normalizeText(
              quoted.productCode
            ),
        };
      }
    );
  };


  const getGuestQuoteSignature = (
    items,
    couponCodes
  ) => {
    return JSON.stringify({
      couponCodes:
        (Array.isArray(couponCodes) ? couponCodes : [couponCodes])
          .map((code) => normalizeText(code).toUpperCase())
          .filter(Boolean),

      items:
        items.map((item) => ({
          id:
            item.id ||
            item.localItemId,

          materialId:
            item.materialId,

          productCode:
            item.productCode,

          widthCm:
            item.widthCm,

          heightCm:
            item.heightCm,

          quantity:
            item.quantity,
        })),
    });
  };


  const quoteGuestCart = async (
    items,
    couponCodes
  ) => {
    const signature =
      getGuestQuoteSignature(
        items,
        couponCodes
      );

    if (
      guestQuoteCache.signature ===
        signature &&
      guestQuoteCache.result
    ) {
      return guestQuoteCache.result;
    }

    if (
      guestQuoteCache.signature ===
        signature &&
      guestQuoteCache.promise
    ) {
      return guestQuoteCache.promise;
    }

    const payload = {
      items:
        items.map(
          buildGuestQuoteItem
        ),
    };

    const normalizedCodes =
      [...new Set((Array.isArray(couponCodes) ? couponCodes : [couponCodes])
        .map((code) => normalizeText(code).toUpperCase())
        .filter((code) => code && code !== AUTOMATIC_COUPON_CODE))]
        .slice(0, 1);

    if (normalizedCodes.length) {
      payload.couponCodes = normalizedCodes;
    }

    guestQuoteCache.signature =
      signature;

    guestQuoteCache.promise =
      (async () => {
        const response =
          await fetch(
            `${API_BASE_URL}/guest-cart`,
            {
              method: "POST",

              headers: {
                "Content-Type":
                  "application/json",

                Accept:
                  "application/json",
              },

              body:
                JSON.stringify(
                  payload
                ),
            }
          );

        const apiPayload =
          await parseApiResponse(
            response,
            "No fue posible cotizar tu carrito."
          );

        const data =
          apiPayload?.data || {};

        const appliedCoupon =
          normalizeCoupon(
            data.coupon
          );

        const appliedCoupons =
          Array.isArray(
            data.coupons
          )
            ? data.coupons
                .map(normalizeCoupon)
                .filter(Boolean)
            : (
                appliedCoupon
                  ? [appliedCoupon]
                  : []
              );

        const removableCoupons =
          appliedCoupons.map((coupon) => ({
            ...coupon,
            removable: true,
          }));

        const mergedItems =
          mergeGuestQuotedItems(
            items,
            data.items
          );

        const fallbackSubtotal =
          mergedItems.reduce((total, item) => {
            const lineTotal = normalizeNumber(item?.lineTotal, 0);
            const calculatedLineTotal =
              normalizeNumber(item?.unitPrice, 0) *
              normalizeNumber(item?.quantity, 0);

            return total + (lineTotal > 0 ? lineTotal : calculatedLineTotal);
          }, 0);

        const fallbackShipping =
          mergedItems.reduce(
            (total, item) => total + normalizeNumber(item?.shippingCost, 0),
            0
          );

        const pricing = normalizePricing(data.pricing, {
          subtotal: fallbackSubtotal,
          discountAmount: 0,
          shippingCost: fallbackShipping,
          total: fallbackSubtotal + fallbackShipping,
        });

        if (pricing.subtotal <= 0 && fallbackSubtotal > 0) {
          pricing.subtotal = fallbackSubtotal;
        }

        if (
          pricing.total <= 0 &&
          pricing.discountAmount <= 0 &&
          fallbackSubtotal > 0
        ) {
          pricing.total = fallbackSubtotal + pricing.shippingCost;
        }

        const result = {
          items: mergedItems,

          coupon:
            removableCoupons[0] ||
            appliedCoupon,

          coupons:
            removableCoupons,

          couponError:
            normalizeCouponError(
              data.couponError
            ),

          pricing,
        };

        if (
          guestQuoteCache.signature ===
          signature
        ) {
          guestQuoteCache.result =
            result;
        }

        return result;
      })();

    try {
      return await guestQuoteCache.promise;
    } catch (error) {
      if (
        guestQuoteCache.signature ===
        signature
      ) {
        guestQuoteCache.signature =
          "";
        guestQuoteCache.result =
          null;
      }

      throw error;
    } finally {
      if (
        guestQuoteCache.signature ===
        signature
      ) {
        guestQuoteCache.promise =
          null;
      }
    }
  };


  const fetchAvailableCoupons = async (
    cart
  ) => {
    const items =
      Array.isArray(cart?.items)
        ? cart.items
        : [];

    if (!items.length) {
      availableCouponsCache = {
        signature: "",
        result: [],
        promise: null,
      };

      return [];
    }

    if (getPromotionalCouponCodes(cart).length) {
      availableCouponsCache = {
        signature: "",
        result: [],
        promise: null,
      };

      return [];
    }

    const token =
      getCustomerToken();

    const signature =
      JSON.stringify({
        source:
          token
            ? "customer"
            : "guest",

        email:
          token
            ? ""
            : getGuestEmail(),

        coupon:
          cart?.coupon?.code || "",

        coupons:
          getCartCouponCodes(
            cart
          ),

        items:
          items.map((item) => ({
            id: item.id,
            materialId:
              item.materialId,
            productCode:
              item.productCode,
            widthCm: item.widthCm,
            heightCm: item.heightCm,
            quantity: item.quantity,
          })),
      });

    if (
      availableCouponsCache.signature ===
        signature &&
      Array.isArray(
        availableCouponsCache.result
      )
    ) {
      return availableCouponsCache.result;
    }

    if (
      availableCouponsCache.signature ===
        signature &&
      availableCouponsCache.promise
    ) {
      return availableCouponsCache.promise;
    }

    availableCouponsCache.signature =
      signature;

    availableCouponsCache.promise =
      (async () => {
        const headers = {
          Accept:
            "application/json",
        };

        let response;

        if (token) {
          headers.Authorization =
            `Bearer ${token}`;

          response =
            await window.PeguuAuthClient.authFetch(
              `${API_BASE_URL}/me/cart/coupons`,
              {
                method: "GET",
                headers,
              }
            );
        } else {
          headers["Content-Type"] =
            "application/json";

          const payload = {
            items:
              items.map(
                buildGuestQuoteItem
              ),
          };

          const email =
            getGuestEmail();

          if (email) {
            payload.email = email;
          }

          const excludeCodes =
            getCartCouponCodes(
              cart
            );

          if (excludeCodes.length) {
            payload.excludeCodes =
              excludeCodes;
          }

          response =
            await fetch(
              `${API_BASE_URL}/guest-cart/coupons`,
              {
                method: "POST",
                headers,
                body:
                  JSON.stringify(
                    payload
                  ),
              }
            );
        }

        const apiPayload =
          await parseApiResponse(
            response,
            "No fue posible consultar los cupones disponibles."
          );

        const appliedCodes =
          new Set(
            getCartCouponCodes(
              cart
            )
          );

        const coupons =
          (
            Array.isArray(
              apiPayload?.data?.coupons
            )
              ? apiPayload.data.coupons
                  .map(normalizeCoupon)
                  .filter(Boolean)
              : []
          ).filter((coupon) => {
            const code =
              normalizeText(
                coupon?.code
              ).toUpperCase();

            if (
              !code ||
              code === AUTOMATIC_COUPON_CODE ||
              appliedCodes.has(code)
            ) {
              return false;
            }

            if (
              !token &&
              coupon.allowGuests ===
                false
            ) {
              return false;
            }

            return true;
          });

        if (
          availableCouponsCache.signature ===
          signature
        ) {
          availableCouponsCache.result =
            coupons;
        }

        return coupons;
      })();

    try {
      return await availableCouponsCache.promise;
    } catch (error) {
      if (
        availableCouponsCache.signature ===
        signature
      ) {
        availableCouponsCache.signature =
          "";
        availableCouponsCache.result =
          [];
      }

      if (error?.status === 401) {
        throw error;
      }

      return [];
    } finally {
      if (
        availableCouponsCache.signature ===
        signature
      ) {
        availableCouponsCache.promise =
          null;
      }
    }
  };


  const applyCustomerCoupon = async (
    code,
    token
  ) => {
    const response =
      await window.PeguuAuthClient.authFetch(
        `${API_BASE_URL}/me/cart/coupon`,
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",

            Accept:
              "application/json",

            Authorization:
              `Bearer ${token}`,
          },

          body:
            JSON.stringify({
              couponCode:
                normalizeText(code)
                  .toUpperCase(),
            }),
        }
      );

    await parseApiResponse(
      response,
      "No fue posible aplicar el cupón."
    );

    availableCouponsCache = {
      signature: "",
      result: [],
      promise: null,
    };

    return getCustomerCart(token);
  };


  const removeCustomerCoupon = async (
    token,
    code
  ) => {
    const response =
      await window.PeguuAuthClient.authFetch(
        `${API_BASE_URL}/me/cart/coupon`,
        {
          method: "DELETE",

          headers: {
            "Content-Type":
              "application/json",
            Accept:
              "application/json",

            Authorization:
              `Bearer ${token}`,
          },

          body: JSON.stringify({
            couponCode:
              normalizeText(code).toUpperCase(),
          }),
        }
      );

    const payload =
      await parseApiResponse(
        response,
        "No fue posible quitar el cupón."
      );

    availableCouponsCache = {
      signature: "",
      result: [],
      promise: null,
    };

    const apiCart =
      payload?.data?.cart ||
      payload?.data ||
      null;

    return getCustomerCart(
      token,
      apiCart
    );
  };


  const removeGuestItem = (
    localItemId
  ) => {
    const updatedItems =
      readGuestCart().filter(
        (item) => {
          return (
            String(item.localItemId) !==
            String(localItemId)
          );
        }
      );

    saveGuestCart(updatedItems);

    return updatedItems;
  };


  const updateGuestItem = (
    itemId,
    item
  ) => {
    const items =
      readGuestCart();
    const itemIndex =
      items.findIndex(
        (currentItem) =>
          normalizeText(
            currentItem?.localItemId
          ) ===
          normalizeText(itemId)
      );

    if (itemIndex < 0) {
      throw new Error(
        "El producto que quieres editar ya no está en el carrito."
      );
    }

    const currentItem =
      items[itemIndex];

    items[itemIndex] = {
      ...currentItem,
      ...item,
      localItemId:
        currentItem.localItemId,
      source: "guest",
      createdAt:
        currentItem.createdAt ||
        new Date().toISOString(),
      updatedAt:
        new Date().toISOString(),
    };

    saveGuestCart(items);

    return items[itemIndex];
  };


  /* =========================
     METADATOS DEL CARRITO API
  ========================= */

  const readCustomerMetadata = () => {
    const metadata =
      parseStorageJson(
        window.localStorage,
        CUSTOMER_CART_METADATA_KEY,
        {}
      );

    if (
      !metadata ||
      typeof metadata !== "object" ||
      Array.isArray(metadata)
    ) {
      return {};
    }

    return metadata;
  };

  const readDesignFileMetadata = () => {
    const metadata = parseStorageJson(
      window.localStorage,
      DESIGN_FILE_METADATA_KEY,
      {}
    );

    return (
      metadata &&
      typeof metadata === "object" &&
      !Array.isArray(metadata)
    )
      ? metadata
      : {};
  };


  const saveCustomerMetadata = (
    metadata
  ) => {
    window.localStorage.setItem(
      CUSTOMER_CART_METADATA_KEY,
      JSON.stringify(metadata)
    );
  };


  const saveCustomerItemMetadata = (
    itemId,
    item
  ) => {
    if (!itemId) {
      return;
    }

    const metadata =
      readCustomerMetadata();

    metadata[itemId] = {
      materialName:
        item.materialName ||
        "Stickers personalizados",

      materialImageUrl:
        item.materialImageUrl ||
        "",

      productCode:
        item.productCode ||
        "",

      includedQuantity:
        item.includedQuantity ??
        null,

      shape:
        item.shape ||
        "",

      design:
        item.design ||
        null,

      qrConfig:
        item.qrConfig ||
        item.qr ||
        null,

      updatedAt:
        new Date().toISOString(),
    };

    saveCustomerMetadata(metadata);
  };


  const removeCustomerItemMetadata = (
    itemId
  ) => {
    const metadata =
      readCustomerMetadata();

    if (
      Object.prototype.hasOwnProperty.call(
        metadata,
        itemId
      )
    ) {
      delete metadata[itemId];

      saveCustomerMetadata(metadata);
    }
  };


  /* =========================
     NORMALIZAR ARTÍCULO
     DE INVITADO
  ========================= */

  const normalizeGuestItem = (
    item
  ) => {
    const isSamplePack =
      normalizeText(
        item?.productCode
      ) === "sample_pack";

    const widthCm =
      isSamplePack
        ? null
        : normalizeNumber(
            item?.widthCm,
            0
          );

    const heightCm =
      isSamplePack
        ? null
        : normalizeNumber(
            item?.heightCm,
            0
          );

    const quantity =
      Math.max(
        0,
        Math.trunc(
          normalizeNumber(
            item?.quantity,
            0
          )
        )
      );

    const unitPrice =
      normalizeNumber(
        item?.unitPrice,
        0
      );

    const storedSubtotal =
      normalizeNumber(
        item?.subtotal,
        0
      );

    const calculatedSubtotal =
      unitPrice * quantity;

    const subtotal =
      storedSubtotal > 0
        ? storedSubtotal
        : calculatedSubtotal;

    return {
      id:
        normalizeText(
          item?.localItemId
        ) ||
        createLocalItemId(),

      source:
        "guest",

      materialId:
        normalizeText(
          item?.materialId
        ),

      materialName:
        normalizeText(
          item?.materialName
        ) ||
        "Stickers personalizados",

      materialImageUrl:
        normalizeText(
          item?.materialImageUrl
        ),

      productCode:
        normalizeText(
          item?.productCode
        ),

      widthCm,
      heightCm,
      quantity,

      includedQuantity:
        Math.max(
          0,
          Math.trunc(
            normalizeNumber(
              item?.includedQuantity,
              isSamplePack
                ? 10
                : quantity
            )
          )
        ),

      unitPrice,
      subtotal,

      currency:
        normalizeText(
          item?.currency
        ) || "MXN",

      shippingCost:
        normalizeNumber(
          item?.shippingCost,
          0
        ),

      shippingPending:
        item?.shippingPending === true,

      shape:
        normalizeText(
          item?.shape
        ),

      fileId:
        normalizeText(
          item?.fileId
        ) ||
        null,

      fileId2:
        normalizeText(
          item?.fileId2 ||
          item?.design2?.fileId ||
          item?.secondaryDesign?.fileId
        ) ||
        null,

      design:
        item?.design ||
        null,

      qrConfig:
        item?.qrConfig ||
        item?.qr ||
        null,

      lineTotal:
        subtotal,

      priceValid:
        item?.priceValid !== false,
    };
  };


  /* =========================
     CONSULTAR CARRITO
     DE INVITADO
  ========================= */

  const getGuestCart = async () => {
    const localItems =
      readGuestCart();

    const requestItems =
      localItems.map((item) => {
        const id = normalizeText(
          item?.localItemId
        );

        if (
          normalizeText(
            item?.productCode
          ) === "sample_pack"
        ) {
          return {
            ...(id ? { id } : {}),
            materialId:
              normalizeText(
                item?.materialId
              ),
          };
        }

        return {
          ...(id ? { id } : {}),
          materialId:
            normalizeText(
              item?.materialId
            ),
          widthCm:
            normalizeNumber(
              item?.widthCm,
              0
            ),
          heightCm:
            normalizeNumber(
              item?.heightCm,
              0
            ),
          quantity:
            Math.max(
              0,
              Math.trunc(
                normalizeNumber(
                  item?.quantity,
                  0
                )
              )
            ),
        };
      });

    const response = await fetch(
      `${API_BASE_URL}/guest-cart`,
      {
        method: "POST",
        headers: {
          "Content-Type":
            "application/json",
          Accept:
            "application/json",
        },
        body: JSON.stringify({
          items: requestItems,
        }),
      }
    );

    let payload = null;

    try {
      payload = await response.json();
    } catch (error) {
      payload = null;
    }

    if (!response.ok || !payload?.data) {
      const apiError = new Error(
        payload?.message ||
        "No fue posible consultar tu carrito."
      );

      apiError.status = response.status;
      apiError.code = payload?.code || null;

      throw apiError;
    }

    const apiCart = payload.data;
    const apiItems = Array.isArray(
      apiCart.items
    )
      ? apiCart.items
      : [];
    const localItemsById = new Map(
      localItems.map((item) => [
        normalizeText(item?.localItemId),
        item,
      ])
    );

    const items = apiItems.map(
      (apiItem, index) => {
        const apiItemId = normalizeText(
          apiItem?.id
        );
        const localItem =
          localItemsById.get(apiItemId) ||
          localItems[index] ||
          {};
        const normalizedItem =
          normalizeGuestItem(localItem);
        const isSamplePack =
          normalizeText(
            apiItem?.productCode ||
            normalizedItem.productCode
          ) === "sample_pack";

        return {
          ...normalizedItem,
          id:
            apiItemId ||
            normalizedItem.id,
          materialName:
            normalizeText(
              apiItem?.materialName
            ) ||
            normalizedItem.materialName,
          productCode:
            normalizeText(
              apiItem?.productCode
            ) ||
            normalizedItem.productCode,
          includedQuantity:
            isSamplePack &&
            apiItem?.quantity != null
              ? Math.max(
                  0,
                  Math.trunc(
                    normalizeNumber(
                      apiItem.quantity,
                      normalizedItem.includedQuantity
                    )
                  )
                )
              : normalizedItem.includedQuantity,
          unitPrice:
            normalizeNumber(
              apiItem?.unitPrice,
              0
            ),
          subtotal:
            normalizeNumber(
              apiItem?.lineTotal,
              0
            ),
          lineTotal:
            normalizeNumber(
              apiItem?.lineTotal,
              0
            ),
          priceValid:
            apiItem?.priceValid === true,
        };
      }
    );

    const pricing = apiCart.pricing || {};

    return {
      id: null,
      status: "active",
      source: "guest",
      items,
      pricing: {
        subtotal:
          normalizeNumber(
            pricing.subtotal,
            0
          ),
        shippingCost:
          normalizeNumber(
            pricing.shippingCost,
            0
          ),
        freeShipping:
          pricing.freeShipping === true,
        freeShippingThreshold:
          normalizeNumber(
            pricing.freeShippingThreshold,
            0
          ),
        total:
          normalizeNumber(
            pricing.total,
            0
          ),
      },
    };
  };


  /* =========================
    CONSULTAR CARRITO
    DEL CLIENTE
  ========================= */

  const getCustomerCart = async (
    token,
    providedApiCart = null
  ) => {
    let apiCart =
      providedApiCart;

    if (
      !apiCart ||
      typeof apiCart !== "object" ||
      Array.isArray(apiCart)
    ) {
      const response =
        await window.PeguuAuthClient.authFetch(
          `${API_BASE_URL}/me/cart`,
          {
            method: "GET",

            headers: {
              Accept:
                "application/json",

              Authorization:
                `Bearer ${token}`,
            },
          }
        );

      const payload =
        await parseApiResponse(
          response,
          "No fue posible consultar tu carrito."
        );

      apiCart =
        payload?.data ||
        {};
    }

    const apiItems =
      Array.isArray(apiCart.items)
        ? apiCart.items
        : [];

    const customerMetadata =
      readCustomerMetadata();

    const designFileMetadata =
      readDesignFileMetadata();

    const items =
      apiItems.map((item) => {
        const itemId =
          getCartItemId(item);

        const localMetadata =
          customerMetadata[itemId] || {};

        const fileId = normalizeText(
          item.fileId ||
          item.file_id ||
          item.designFileId ||
          item.design?.fileId ||
          item.design?.file_id ||
          item.file?.fileId ||
          item.file?.file_id ||
          item.file?.id
        );

        const storedDesign =
          designFileMetadata[fileId] ||
          null;

        const localDesign =
          localMetadata.design ||
          null;

        const apiDesign =
          getApiItemDesign(item);

        const design =
          localDesign ||
          storedDesign ||
          apiDesign
            ? {
                ...(apiDesign || {}),
                ...(storedDesign || {}),
                ...(localDesign || {}),
                fileId:
                  fileId ||
                  apiDesign?.fileId ||
                  storedDesign?.fileId ||
                  localDesign?.fileId ||
                  null,
                fileUrl:
                  apiDesign?.fileUrl ||
                  apiDesign?.previewUrl ||
                  storedDesign?.fileUrl ||
                  storedDesign?.previewUrl ||
                  localDesign?.fileUrl ||
                  localDesign?.previewUrl ||
                  null,
                previewUrl:
                  apiDesign?.previewUrl ||
                  apiDesign?.fileUrl ||
                  storedDesign?.previewUrl ||
                  storedDesign?.fileUrl ||
                  localDesign?.previewUrl ||
                  localDesign?.fileUrl ||
                  null,
                uploadLater:
                  fileId ||
                  apiDesign?.fileUrl ||
                  apiDesign?.previewUrl
                    ? false
                    : localDesign
                      ?.uploadLater ===
                      true,
              }
            : null;

        const quantity =
          Math.max(
            0,
            Math.trunc(
              normalizeNumber(
                item.quantity,
                0
              )
            )
          );

        return {
          id:
            itemId,

          source:
            "customer",

          materialId:
            normalizeText(
              item.materialId ||
              item.material?.id
            ),

          materialName:
            normalizeText(
              localMetadata.materialName ||
              item.materialName ||
              item.name ||
              item.material?.name
            ) ||
            "Stickers personalizados",

          materialImageUrl:
            normalizeText(
              localMetadata.materialImageUrl ||
              item.materialImageUrl ||
              item.imageUrl ||
              item.material?.imageUrl
            ),

          productCode:
            normalizeText(
              item.productCode ||
              item.material?.productCode ||
              localMetadata.productCode
            ),

          includedQuantity:
            Math.max(
              0,
              Math.trunc(
                normalizeNumber(
                  item.purchase?.quantity ??
                    item.includedQuantity ??
                    localMetadata.includedQuantity,
                  quantity
                )
              )
            ),

          design,

          widthCm:
            item.widthCm == null
              ? null
              : normalizeNumber(item.widthCm, 0),

          heightCm:
            item.heightCm == null
              ? null
              : normalizeNumber(item.heightCm, 0),

          quantity,

          fileId:
            fileId ||
            null,

          fileId2:
            normalizeText(
              item.fileId2 ||
              item.design2?.fileId ||
              item.secondaryDesign?.fileId
            ) ||
            null,

          qrConfig:
            item.qrConfig ||
            null,

          unitPrice:
            normalizeNumber(
              item.unitPrice ??
                item.purchase?.unitPrice,
              0
            ),

          currency:
            normalizeText(
              item.currency ||
              item.purchase?.currency
            ) || "MXN",

          lineTotal:
            normalizeNumber(
              item.lineTotal ??
                item.purchase?.lineTotal,
              0
            ),

          priceValid:
            normalizeText(
              item.productCode ||
              item.material?.productCode ||
              localMetadata.productCode
            ) === "sample_pack"
              ? item.priceValid !== false
              : item.priceValid === true,

          sortOrder:
            normalizeNumber(
              item.sortOrder,
              0
            ),

          createdAt:
            item.createdAt ||
            null,

          updatedAt:
            item.updatedAt ||
            null,
        };
      });

    /*
    * Se respeta el orden enviado
    * por la API.
    */

    items.sort(
      (firstItem, secondItem) => {
        return (
          firstItem.sortOrder -
          secondItem.sortOrder
        );
      }
    );

    const appliedCoupon =
      normalizeCoupon(
        apiCart.coupon
      );

    const appliedCoupons =
      Array.isArray(
        apiCart.coupons
      )
        ? apiCart.coupons
            .map(normalizeCoupon)
            .filter(Boolean)
        : (
            appliedCoupon
              ? [appliedCoupon]
              : []
          );
    const couponsWithOrigin =
      appliedCoupons.map((coupon) => ({
        ...coupon,
        removable:
          coupon.code !==
            AUTOMATIC_COUPON_CODE,
      }));

    return {
      id:
        normalizeText(
          apiCart.id
        ) ||
        null,

      customerId:
        normalizeText(
          apiCart.customerId
        ) ||
        null,

      status:
        normalizeText(
          apiCart.status
        ) ||
        "active",

      source:
        "customer",

      createdAt:
        apiCart.createdAt ||
        null,

      updatedAt:
        apiCart.updatedAt ||
        null,

      items,

      coupon:
        couponsWithOrigin[0] ||
        appliedCoupon,

      coupons:
        couponsWithOrigin,

      couponError:
        normalizeCouponError(
          apiCart.couponError
        ),

      pricing: {
        subtotal:
          normalizeNumber(
            apiCart.pricing?.subtotal,
            0
          ),

        discountAmount:
          normalizeNumber(
            apiCart.pricing
              ?.discountAmount,
            0
          ),

        shippingCost:
          normalizeNumber(
            apiCart.pricing?.shippingCost,
            0
          ),

        freeShipping:
          apiCart.pricing?.freeShipping ===
          true,

        freeShippingThreshold:
          normalizeNumber(
            apiCart.pricing
              ?.freeShippingThreshold,
            0
          ),

        total:
          normalizeNumber(
            apiCart.pricing?.total,
            0
          ),
      },
    };
  };

  /* =========================
     CONSULTAR CARRITO ACTUAL
  ========================= */

  const getCurrentCart = async () => {
    const token =
      getCustomerToken();

    if (token) {
      return getCustomerCart(
        token
      );
    }

    const items =
      readGuestCart().map(
        normalizeGuestItem
      );

    const couponCodes =
      readGuestCouponCodes();

    const subtotal =
      items.reduce(
        (total, item) =>
          total + item.lineTotal,
        0
      );

    const shippingCost =
      items.reduce(
        (total, item) =>
          total + item.shippingCost,
        0
      );

    const localCart = {
      id:
        null,

      status:
        "active",

      source:
        "guest",

      items,

      coupon:
        null,

      coupons:
        [],

      couponError:
        null,

      pricing: {
        subtotal,
        discountAmount: 0,
        shippingCost,
        freeShipping: false,
        freeShippingThreshold: null,
        total: subtotal,
      },
    };

    if (!items.length) {
      return localCart;
    }

    try {
      const quoted =
        await quoteGuestCart(
          items,
          couponCodes
        );

      couponCodes.forEach((code) => {
        if (!quotedCouponApplies(quoted, code)) {
          removeGuestCouponCode(code);
        }
      });

      return {
        ...localCart,
        items:
          quoted.items || items,
        coupon:
          quoted.coupon,
        coupons:
          Array.isArray(
            quoted.coupons
          )
            ? quoted.coupons
            : quoted.coupon
              ? [quoted.coupon]
              : [],
        couponError:
          quoted.couponError,
        pricing:
          quoted.pricing ||
          localCart.pricing,
      };
    } catch (error) {
      console.warn(
        "Peguu Cart: no fue posible cotizar el carrito de invitado.",
        error
      );

      if (couponCodes.length) {
        localCart.couponError =
          error?.status === 429
            ? "Demasiados intentos con cupones. Espera un momento y vuelve a intentar."
            : error?.message ||
              "No fue posible aplicar el cupón.";
      }

      return localCart;
    }
  };


  /* =========================
     CONSERVAR CARRITO AL SALIR
  ========================= */

  const preserveCustomerCartAsGuest =
    async () => {
      const token =
        getCustomerToken();

      if (!token) {
        return readGuestCart();
      }

      let customerCart;

      try {
        customerCart =
          await getCustomerCart(token);
      } catch (error) {
        const renderedCart =
          window.peguuCartDrawer
            ?.currentCart;

        if (
          renderedCart?.source !==
            "customer" ||
          !Array.isArray(
            renderedCart.items
          )
        ) {
          throw error;
        }

        customerCart =
          renderedCart;
      }

      const existingItems =
        readGuestCart();
      const preservedOrigins =
        new Set(
          existingItems
            .map((item) =>
              normalizeText(
                item
                  ?.originCustomerItemId
              )
            )
            .filter(Boolean)
        );

      const preservedItems =
        (Array.isArray(
          customerCart?.items
        )
          ? customerCart.items
          : []
        )
          .filter((item) => {
            const itemId =
              normalizeText(item?.id);

            return (
              !itemId ||
              !preservedOrigins.has(
                itemId
              )
            );
          })
          .map((item) => ({
            ...item,
            localItemId:
              createLocalItemId(),
            source: "guest",
            subtotal:
              normalizeNumber(
                item?.lineTotal ??
                  item?.subtotal,
                0
              ),
            originCustomerId:
              normalizeText(
                customerCart
                  ?.customerId
              ) || null,
            originCustomerCartId:
              normalizeText(
                customerCart?.id
              ) || null,
            originCustomerItemId:
              normalizeText(
                item?.id
              ) || null,
          }));

      const nextItems = [
        ...existingItems,
        ...preservedItems,
      ];

      saveGuestCart(nextItems);

      return nextItems;
    };


  /* =========================
     AGREGAR AL CARRITO API
  ========================= */

  const addCustomerSamplePack = async (
    material,
    token
  ) => {
    const materialId =
      normalizeText(
        material.materialId ||
        material.id
      );

    if (!materialId) {
      throw new Error(
        "No fue posible identificar el paquete de muestras."
      );
    }

    const response = await window.PeguuAuthClient.authFetch(
      `${API_BASE_URL}/me/cart/items`,
      {
        method: "POST",

        headers: {
          "Content-Type":
            "application/json",

          Accept:
            "application/json",

          Authorization:
            `Bearer ${token}`,
        },

        body: JSON.stringify({
          materialId:
            materialId,
        }),
      }
    );

    const payload =
      await parseApiResponse(
        response,
        "No fue posible agregar el paquete de muestras al carrito."
      );

    const apiItem = payload?.data;
    const itemId = getCartItemId(apiItem);

    if (itemId) {
      saveCustomerItemMetadata(
        itemId,
        {
          materialName:
            material.name ||
            material.materialName,
          materialImageUrl:
            material.imageUrl ||
            material.materialImageUrl,
          productCode:
            "sample_pack",
          includedQuantity:
            material.purchase?.quantity ??
            material.includedQuantity ??
            10,
        }
      );
    }

    return apiItem;
  };


  const addGuestSamplePack = (
    material
  ) => {
    const items = readGuestCart();
    const existingItem = items.find(
      (item) =>
        item?.productCode ===
          "sample_pack" ||
        String(item?.materialId) ===
          String(material.id)
    );

    if (existingItem) {
      return existingItem;
    }

    const purchase =
      material.purchase || {};

    const item = {
      localItemId:
        createLocalItemId(),
      source: "guest",
      materialId:
        material.id,
      materialName:
        material.name ||
        "Paquete de muestras",
      materialImageUrl:
        material.imageUrl || "",
      productCode:
        "sample_pack",
      widthCm: null,
      heightCm: null,
      quantity: 1,
      includedQuantity:
        normalizeNumber(
          purchase.quantity,
          10
        ),
      unitPrice:
        normalizeNumber(
          purchase.unitPrice,
          99
        ),
      subtotal:
        normalizeNumber(
          purchase.lineTotal,
          99
        ),
      shippingCost: 0,
      shippingPending: true,
      fileId: null,
      fileId2: null,
      design: null,
      qrConfig: null,
      currency:
        purchase.currency ||
        "MXN",
      priceValid: true,
      createdAt:
        new Date().toISOString(),
      updatedAt:
        new Date().toISOString(),
    };

    items.push(item);
    saveGuestCart(items);

    return item;
  };

  const addCustomerCartItem = async (
    item,
    token
  ) => {
    const response =
      await window.PeguuAuthClient.authFetch(
        `${API_BASE_URL}/me/cart/items`,
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",

            Accept:
              "application/json",

            Authorization:
              `Bearer ${token}`,
          },

          body:
            JSON.stringify({
              materialId:
                item.materialId,

              widthCm:
                item.widthCm,

              heightCm:
                item.heightCm,

              quantity:
                item.quantity,

              fileId:
                item.fileId ||
                null,

              qrConfig:
                item.qrConfig ||
                null,
            }),
        }
      );

    const payload =
      await parseApiResponse(
        response,
        "No fue posible transferir el producto al carrito de tu cuenta."
      );

    const apiItem =
      payload?.data;

    if (!apiItem?.id) {
      throw new Error(
        "La API no devolvió el identificador del producto agregado."
      );
    }

    saveCustomerItemMetadata(
      apiItem.id,
      item
    );

    return apiItem;
  };


  const updateCustomerCartItem = async (
    itemId,
    item,
    token
  ) => {
    const response =
      await window.PeguuAuthClient.authFetch(
        `${API_BASE_URL}/me/cart/items/${encodeURIComponent(itemId)}`,
        {
          method: "PUT",
          headers: {
            "Content-Type":
              "application/json",
            Accept:
              "application/json",
            Authorization:
              `Bearer ${token}`,
          },
          body: JSON.stringify({
            materialId:
              item.materialId,
            widthCm:
              item.widthCm,
            heightCm:
              item.heightCm,
            quantity:
              item.quantity,
            fileId:
              item.fileId || null,
            fileId2:
              item.fileId2 || null,
            qrConfig:
              item.productCode ===
                "qr_sticker"
                ? item.qrConfig
                : null,
          }),
        }
      );

    await parseApiResponse(
      response,
      "No fue posible actualizar el producto."
    );

    saveCustomerItemMetadata(
      itemId,
      item
    );

    /*
     * PUT devuelve únicamente el ítem. El carrito completo se consulta
     * enseguida para recuperar pricing, cupones, envío y total vigentes.
     */
    return getCustomerCart(token);
  };


  /* =========================
     FUSIONAR CARRITO INVITADO
  ========================= */

  let guestCartMergePromise =
    null;


  const mergeGuestCartIntoCustomerCart =
    async () => {
      if (guestCartMergePromise) {
        return guestCartMergePromise;
      }

      const token =
        getCustomerToken();

      const guestItems =
        readGuestCart();

      if (
        !token ||
        guestItems.length === 0
      ) {
        return {
          attempted: 0,
          transferred: 0,
          failed: [],
          cart: null,
        };
      }

      guestCartMergePromise =
        (async () => {
          /*
           * Esta consulta confirma primero que la
           * sesión y el carrito remoto siguen activos.
           */
          const customerCart =
            await getCustomerCart(token);

          const failedItems = [];
          let transferred = 0;

          for (
            const rawItem of guestItems
          ) {
            const item =
              normalizeGuestItem(
                rawItem
              );

            try {
              const originCustomerId =
                normalizeText(
                  rawItem
                    ?.originCustomerId
                );
              const originCustomerCartId =
                normalizeText(
                  rawItem
                    ?.originCustomerCartId
                );
              const originCustomerItemId =
                normalizeText(
                  rawItem
                    ?.originCustomerItemId
                );
              const belongsToCurrentCart =
                (
                  originCustomerId &&
                  originCustomerId ===
                    normalizeText(
                      customerCart
                        ?.customerId
                    )
                ) ||
                (
                  originCustomerCartId &&
                  originCustomerCartId ===
                    normalizeText(
                      customerCart?.id
                    )
                );
              const alreadyInCustomerCart =
                belongsToCurrentCart &&
                originCustomerItemId &&
                customerCart.items.some(
                  (customerItem) =>
                    normalizeText(
                      customerItem?.id
                    ) ===
                    originCustomerItemId
                );

              if (
                alreadyInCustomerCart
              ) {
                removeGuestItem(
                  item.id
                );
                transferred += 1;
                continue;
              }

              if (
                item.productCode ===
                  "sample_pack"
              ) {
                await addCustomerSamplePack(
                  item,
                  token
                );
              } else {
                await addCustomerCartItem(
                  item,
                  token
                );
              }

              /*
               * Se elimina únicamente después de que
               * Peguu One confirma la nueva línea.
               */
              removeGuestItem(
                item.id
              );

              transferred += 1;
            } catch (error) {
              if (error?.status === 401) {
                throw error;
              }

              failedItems.push({
                id: item.id,
                message:
                  error?.message ||
                  "No fue posible agregar este producto.",
              });
            }
          }

          const updatedCart =
            await getCustomerCart(token);

          const guestCouponCodes =
            readGuestCouponCodes();

          if (
            guestCouponCodes.length &&
            transferred > 0
          ) {
            for (const guestCouponCode of guestCouponCodes) {
              try {
                const couponCart =
                  await applyCustomerCoupon(
                    guestCouponCode,
                    token
                  );

                rememberManualCustomerCoupon(
                  guestCouponCode,
                  couponCart
                );
              } catch (error) {
                if (error?.status === 401) {
                  throw error;
                }

                console.warn(
                  "Peguu Cart: no fue posible transferir un cupón del invitado.",
                  error
                );
              }
            }
          }

          if (!readGuestCart().length) {
            clearGuestCouponCode();
          }

          const mergedCart =
            await getCustomerCart(token);

          document.dispatchEvent(
            new CustomEvent(
              "peguu:cart:merged",
              {
                detail: {
                  attempted:
                    guestItems.length,
                  transferred,
                  failedCount:
                    failedItems.length,
                  cart:
                    mergedCart,
                },
              }
            )
          );

          return {
            attempted:
              guestItems.length,
            transferred,
            failed:
              failedItems,
            cart:
              mergedCart,
          };
        })();

      try {
        return await guestCartMergePromise;
      } finally {
        guestCartMergePromise =
          null;
      }
    };


  /* =========================
     CLASE PRINCIPAL
  ========================= */

  class PeguuCartDrawer {
    constructor(root) {
      this.root =
        root;

      this.panel =
        root.querySelector(
          "[data-peguu-cart-panel]"
        );

      this.countElement =
        root.querySelector(
          "[data-peguu-cart-count]"
        );

      this.emptyState =
        root.querySelector(
          "[data-peguu-cart-empty]"
        );

      this.filledState =
        root.querySelector(
          "[data-peguu-cart-filled]"
        );

      this.productsContainer =
        root.querySelector(
          "[data-peguu-cart-products]"
        );

      this.subtotalLabel =
        root.querySelector(
          "[data-peguu-cart-subtotal-label]"
        );

      this.subtotalElement =
        root.querySelector(
          "[data-peguu-cart-subtotal]"
        );

      this.discountRow =
        root.querySelector(
          "[data-peguu-cart-discount-row]"
        );

      this.discountLabel =
        root.querySelector(
          "[data-peguu-cart-discount-label]"
        );

      this.discountElement =
        root.querySelector(
          "[data-peguu-cart-discount]"
        );

      this.shippingElement =
        root.querySelector(
          "[data-peguu-cart-shipping]"
        );

      this.totalElement =
        root.querySelector(
          "[data-peguu-cart-total]"
        );

      this.loadingElement =
        root.querySelector(
          "[data-peguu-cart-loading]"
        );

      this.loadingTextElement =
        root.querySelector(
          "[data-peguu-cart-loading-text]"
        );

      this.errorElement =
        root.querySelector(
          "[data-peguu-cart-error]"
        );

      this.checkoutButton =
        root.querySelector(
          "[data-peguu-cart-checkout]"
        );

      this.materialsUrl =
        root.dataset.materialsUrl ||
        "/pages/materiales";

      this.paymentUrl =
        root.dataset.paymentUrl ||
        "/pages/arma-tus-stickers-resumen?open-payment=1";

      this.multiPaymentUrl =
        root.dataset.multiPaymentUrl ||
        "/pages/arma-tus-stickers-resumen?open-payment=1";

      this.trashIconUrl =
        normalizeText(
          root.dataset.trashIconUrl
        );

      this.editIconUrl =
        normalizeText(
          root.dataset.editIconUrl
        );

      this.isOpen =
        false;

      this.isLoading =
        false;

      this.refreshPromise =
        null;

      this.lastCartRenderAt =
        0;

      this.initialCartSyncPromise =
        null;

      this.lastFocusedElement =
        null;

      this.currentCart = {
        source: "guest",
        items: [],
      };

      this.bindEvents();

      this.initialCartSyncPromise =
        this.synchronizeAuthenticatedCart({
          silent: true,
        });
    }


    /* =========================
       EVENTOS
    ========================= */

    bindEvents() {
      document.addEventListener(
        "click",
        (event) => {
          const openButton =
            event.target.closest(
              "[data-peguu-cart-open]"
            );

          if (openButton) {
            event.preventDefault();

            this.open();

            return;
          }

          const checkoutButton =
            event.target.closest(
              "[data-peguu-cart-checkout]"
            );

          if (
            checkoutButton &&
            checkoutButton.getAttribute(
              "aria-disabled"
            ) === "true"
          ) {
            event.preventDefault();
            return;
          }

          if (
            checkoutButton &&
            this.root.contains(
              checkoutButton
            )
          ) {
            event.preventDefault();

            this.navigateToCheckout();

            return;
          }


          const closeButton =
            event.target.closest(
              "[data-peguu-cart-close]"
            );

          if (closeButton) {
            event.preventDefault();

            this.close();

            return;
          }


          const removeButton =
            event.target.closest(
              "[data-peguu-cart-remove]"
            );

          const editButton =
            event.target.closest(
              "[data-peguu-cart-edit]"
            );

          if (
            editButton &&
            this.root.contains(
              editButton
            )
          ) {
            event.preventDefault();

            const itemId =
              editButton.dataset.itemId;
            const item =
              this.currentCart?.items
                ?.find(
                  (currentItem) =>
                    normalizeText(
                      currentItem?.id
                    ) ===
                    normalizeText(itemId)
                );

            if (item) {
              beginCartItemEdit(
                item,
                "/pages/arma-tus-stickers-resumen"
              );
            }

            return;
          }

          if (
            removeButton &&
            this.root.contains(
              removeButton
            )
          ) {
            event.preventDefault();

            const itemId =
              removeButton.dataset.itemId;

            const source =
              removeButton.dataset.source;

            if (itemId) {
              this.removeItem(
                itemId,
                source,
                removeButton
              );
            }
          }
        }
      );


      document.addEventListener(
        "keydown",
        (event) => {
          if (!this.isOpen) {
            return;
          }

          if (
            event.key ===
            "Escape"
          ) {
            event.preventDefault();

            this.close();

            return;
          }

          if (
            event.key ===
            "Tab"
          ) {
            this.trapFocus(
              event
            );
          }
        }
      );


      document.addEventListener(
        "peguu:cart:open",
        () => {
          this.open();
        }
      );


      document.addEventListener(
        "peguu:cart:refresh",
        () => {
          this.refresh();
        }
      );


      document.addEventListener(
        "peguu:cart:merged",
        (event) => {
          const mergedCart =
            event.detail?.cart;

          if (mergedCart) {
            this.currentCart =
              mergedCart;

            this.render(
              mergedCart
            );

            return;
          }

          this.refresh({
            silent: true,
          });
        }
      );


      window.addEventListener(
        "peguu:customer-authenticated",
        () => {
          this.synchronizeAuthenticatedCart();
        }
      );


      window.addEventListener(
        "peguu:customer-logged-out",
        () => {
          this.clearError();

          this.refresh({
            silent: true,
          });
        }
      );


      window.addEventListener(
        "storage",
        (event) => {
          const relevantKeys = [
            GUEST_CART_STORAGE_KEY,
            AUTH_STORAGE_KEY,
            CUSTOMER_CART_METADATA_KEY,
          ];

          if (
            relevantKeys.includes(
              event.key
            )
          ) {
            if (
              event.key ===
                AUTH_STORAGE_KEY &&
              event.newValue
            ) {
              this.synchronizeAuthenticatedCart({
                silent: true,
              });

              return;
            }

            this.refresh({
              silent: true,
            });
          }
        }
      );
    }


    /* =========================
       SINCRONIZAR SESIÓN
    ========================= */

    async synchronizeAuthenticatedCart(
      options = {}
    ) {
      const silent =
        options.silent === true;

      const hasToken =
        Boolean(
          getCustomerToken()
        );

      const hasGuestItems =
        readGuestCart().length > 0;

      if (
        !hasToken ||
        !hasGuestItems
      ) {
        await this.refresh({
          silent,
        });

        return;
      }

      this.clearError();

      this.setLoading(
        !silent,
        "Estamos agregando tus productos anteriores a tu cuenta…"
      );

      try {
        const result =
          await mergeGuestCartIntoCustomerCart();

        if (result.cart) {
          this.currentCart =
            result.cart;

          this.render(
            result.cart
          );
        } else {
          await this.refresh({
            silent: true,
          });
        }

        if (result.failed.length) {
          this.showError(
            "No pudimos agregar algunos productos a tu cuenta. Permanecen guardados en este dispositivo para que no los pierdas."
          );
        }
      } catch (error) {
        let message =
          "No fue posible sincronizar tu carrito. Tus productos permanecen guardados en este dispositivo.";

        if (error?.status === 401) {
          message =
            "Tu sesión expiró. Inicia sesión nuevamente; tus productos permanecen guardados en este dispositivo.";
        }

        if (!silent) {
          this.showError(message);
        }

        document.dispatchEvent(
          new CustomEvent(
            "peguu:cart:merge-failed",
            {
              detail: {
                error
              }
            }
          )
        );
      } finally {
        this.setLoading(false);
      }
    }


    /* =========================
       ABRIR / CERRAR
    ========================= */

    async open() {
      if (this.isOpen) {
        return;
      }

      this.lastFocusedElement =
        document.activeElement;

      this.isOpen =
        true;

      this.root.classList.add(
        "is-open"
      );

      this.root.setAttribute(
        "aria-hidden",
        "false"
      );

      document.body.classList.add(
        "peguu-cart-is-open"
      );

      if (
        this.initialCartSyncPromise
      ) {
        await this.initialCartSyncPromise;
        this.initialCartSyncPromise =
          null;
      }

      const hasRecentCart =
        this.lastCartRenderAt > 0 &&
        Date.now() -
          this.lastCartRenderAt <
          CART_OPEN_CACHE_TTL_MS;

      if (!hasRecentCart) {
        await this.refresh({
          silent:
            this.lastCartRenderAt > 0,
        });
      }

      window.requestAnimationFrame(
        () => {
          this.panel?.focus();
        }
      );
    }


    close() {
      if (!this.isOpen) {
        return;
      }

      this.isOpen =
        false;

      this.root.classList.remove(
        "is-open"
      );

      this.root.setAttribute(
        "aria-hidden",
        "true"
      );

      document.body.classList.remove(
        "peguu-cart-is-open"
      );

      if (
        this.lastFocusedElement &&
        typeof this.lastFocusedElement
          .focus ===
        "function"
      ) {
        this.lastFocusedElement.focus();
      }
    }


    /* =========================
       ACTUALIZAR CARRITO
    ========================= */

    async refresh(
      options = {}
    ) {
      if (this.refreshPromise) {
        return this.refreshPromise;
      }

      if (this.isLoading) {
        return;
      }

      const silent =
        options.silent ===
        true;

      const refreshRequest =
        (async () => {
          if (!silent) {
            this.setLoading(true);
          }

          this.clearError();

          try {
            const cart =
              await getCurrentCart();

            this.currentCart =
              cart;

            this.render(cart);

            return cart;
          } catch (error) {
            if (!silent) {
              console.error(
                "Peguu Cart Drawer: no fue posible actualizar el carrito."
              );
            }

            let message =
              error.message ||
              "No pudimos actualizar tu carrito.";

            if (
              error?.status === 401
            ) {
              message =
                "Tu sesión expiró. Inicia sesión nuevamente.";
            }

            if (!silent) {
              this.showError(message);
            }

            if (
              options.throwOnError === true
            ) {
              throw error;
            }
          } finally {
            if (!silent) {
              this.setLoading(false);
            }
          }
        })();

      this.refreshPromise =
        refreshRequest;

      try {
        return await refreshRequest;
      } finally {
        if (
          this.refreshPromise ===
          refreshRequest
        ) {
          this.refreshPromise =
            null;
        }
      }
    }


    /* =========================
       NAVEGAR AL CHECKOUT
    ========================= */

    async navigateToCheckout() {
      if (this.isLoading) {
        return;
      }

      const customerToken =
        getCustomerToken();

      const currentItems =
        Array.isArray(
          this.currentCart?.items
        )
          ? this.currentCart.items
          : [];

      /*
       * El drawer ya mantiene sincronizado el carrito del cliente. Si hay
       * sesión, reutilizamos ese estado para entrar al bloque final sin una
       * segunda consulta ni volver a mostrar el estado de actualización.
       */
      if (
        customerToken &&
        currentItems.length &&
        !currentItems.some(
          (item) =>
            item?.priceValid === false
        )
      ) {
        window.location.assign(
          currentItems.length > 1 ||
          currentItems[0]?.productCode ===
            "sample_pack"
            ? this.multiPaymentUrl
            : this.paymentUrl
        );

        return;
      }

      this.clearError();
      this.setLoading(
        true,
        "Validando carrito..."
      );

      try {
        const cart =
          await getCurrentCart();

        this.currentCart =
          cart;

        this.render(cart);

        const items =
          Array.isArray(
            cart?.items
          )
            ? cart.items
            : [];

        if (!items.length) {
          this.showError(
            "Tu carrito está vacío."
          );

          return;
        }

        if (
          items.some(
            (item) =>
              item?.priceValid ===
              false
          )
        ) {
          this.showError(
            "No es posible continuar al pago porque uno o más productos requieren actualizar su precio."
          );

          return;
        }

        window.location.assign(
          items.length > 1 ||
          items[0]?.productCode ===
            "sample_pack"
            ? this.multiPaymentUrl
            : this.paymentUrl
        );
      } catch (error) {
        console.error(
          "Peguu Cart Drawer: no fue posible validar el carrito antes del pago.",
          error
        );

        this.showError(
          error?.message ||
          "No pudimos validar tu carrito. Inténtalo nuevamente."
        );
      } finally {
        this.setLoading(false);
      }
    }


    /* =========================
       ELIMINAR PRODUCTO
    ========================= */

    async removeItem(
      itemId,
      source,
      button
    ) {
      if (this.isLoading) {
        return;
      }

      this.clearError();

      if (button) {
        button.disabled =
          true;
      }

      try {
        /*
         * INVITADO:
         * se elimina de localStorage
         * y de la interfaz inmediatamente.
         */

        if (
          source !==
          "customer"
        ) {
          removeGuestItem(itemId);

          const cart =
            await getCurrentCart();

          this.currentCart = cart;

          this.render(cart);

          document.dispatchEvent(
            new CustomEvent(
              "peguu:cart:updated",
              {
                detail: {
                  itemId,
                  source:
                    "guest",

                  action:
                    "remove",
                },
              }
            )
          );

          return;
        }


        /*
         * CLIENTE AUTENTICADO:
         * se espera la respuesta de la API.
         */

        this.setLoading(true);

        await this.removeCustomerItem(
          itemId
        );

        const cart =
          await getCurrentCart();

        this.currentCart = cart;

        this.render(cart);

        document.dispatchEvent(
          new CustomEvent(
            "peguu:cart:updated",
            {
              detail: {
                itemId,
                source:
                  "customer",

                action:
                  "remove",
              },
            }
          )
        );
      } catch (error) {
        console.error(
          "Peguu Cart Drawer:",
          error
        );

        this.showError(
          error.message ||
          "No fue posible eliminar el producto."
        );
      } finally {
        this.setLoading(false);

        if (
          button?.isConnected
        ) {
          button.disabled =
            false;
        }
      }
    }


    async removeCustomerItem(
      itemId
    ) {
      const token =
        getCustomerToken();

      if (!token) {
        throw new Error(
          "Tu sesión ya no está activa."
        );
      }

      const encodedItemId =
        encodeURIComponent(
          itemId
        );

      const response =
        await window.PeguuAuthClient.authFetch(
          `${API_BASE_URL}/me/cart/items/${encodedItemId}`,
          {
            method:
              "DELETE",

            headers: {
              Accept:
                "application/json",

              Authorization:
                `Bearer ${token}`,
            },
          }
        );

      await parseApiResponse(
        response,
        "No fue posible eliminar el producto."
      );

      removeCustomerItemMetadata(
        itemId
      );
    }


    /* =========================
       RENDER
    ========================= */

    render(cart) {
      this.lastCartRenderAt =
        Date.now();

      const items =
        Array.isArray(cart.items)
          ? cart.items
          : [];

      const hasItems =
        items.length > 0;

      if (!hasItems) {
        window.sessionStorage.removeItem(
          PAYMENT_STORAGE_KEY
        );
      }

      const itemCount =
        items.length;

      const stickerPieces =
        this.calculateStickerPieces(
          items
        );

      const pricing =
        cart.pricing || {};

      const subtotal =
        normalizeNumber(
          pricing.subtotal,
          0
        );

      const shippingCost =
        normalizeNumber(
          pricing.shippingCost,
          0
        );

      const total =
        normalizeNumber(
          pricing.total,
          subtotal + shippingCost
        );

      const hasInvalidPrice =
        items.some(
          (item) =>
            item.priceValid === false
        );


      if (this.countElement) {
        this.countElement.textContent =
          `(${itemCount})`;
      }


      if (this.emptyState) {
        this.emptyState.hidden =
          hasItems;
      }


      if (this.filledState) {
        this.filledState.hidden =
          !hasItems;
      }


      if (
        this.productsContainer
      ) {
        this.productsContainer.innerHTML =
          hasItems
            ? items
                .map(
                  (item) =>
                    this.renderItem(
                      item
                    )
                )
                .join("")
            : "";

        void hydrateLocalDesignPreviews(
          this.productsContainer
        );
      }


      if (
        this.subtotalLabel
      ) {
        const piecesText =
          stickerPieces.toLocaleString(
            "es-MX"
          );

        this.subtotalLabel.textContent =
          `Subtotal (${piecesText} pz)`;
      }


      if (
        this.subtotalElement
      ) {
        this.subtotalElement.textContent =
          formatMoney(subtotal);
      }


      const discountAmount =
        normalizeNumber(
          pricing.discountAmount,
          0
        );

      if (this.discountRow) {
        this.discountRow.hidden =
          discountAmount <= 0;
      }

      if (this.discountLabel) {
        const couponCodes =
          getCartCouponCodes(cart);

        this.discountLabel.textContent =
          couponCodes.length
            ? `Descuento (${couponCodes.join(", ")})`
            : "Descuento";
      }

      if (this.discountElement) {
        this.discountElement.textContent =
          discountAmount > 0
            ? `−${formatMoney(discountAmount)}`
            : formatMoney(0);
      }


      if (
        this.shippingElement
      ) {
        const freeShipping =
          pricing.freeShipping === true;

        this.shippingElement.textContent =
          freeShipping
            ? "Gratis"
            : "Con costo";

        this.shippingElement.classList.toggle(
          "is-free",
          freeShipping
        );
      }


      if (
        this.totalElement
      ) {
        const formattedTotal =
          formatMoney(total);

        this.totalElement.textContent =
          /\bMXN\b/i.test(
            formattedTotal
          )
            ? formattedTotal
            : `${formattedTotal} MXN`;
      }


      this.updateGlobalCartCounters(
        itemCount
      );

      this.updateCheckoutState(
        hasItems &&
        !hasInvalidPrice
      );

      if (hasInvalidPrice) {
        this.showError(
          "No es posible continuar al pago porque uno o más productos requieren actualizar su precio."
        );
      }
    }


    renderItem(item) {
      const isSamplePack =
        item.productCode === "sample_pack";
      const materialName =
        normalizeText(
          item.materialName
        ) ||
        "Stickers personalizados";

      const quantity =
        Math.max(
          0,
          Math.trunc(
            normalizeNumber(
              item.quantity,
              0
            )
          )
        );

      const widthCm = isSamplePack ? 0 : normalizeNumber(item.widthCm, 0);

      const heightCm = isSamplePack ? 0 : normalizeNumber(item.heightCm, 0);

      const shape =
        normalizeText(
          item.shape
        );

      const size =
        widthCm > 0 &&
        heightCm > 0
          ? `${widthCm}x${heightCm} cm`
          : isSamplePack
            ? "Medidas: No aplica"
            : "Medidas por definir";

      const quantityText = isSamplePack
        ? `1 paquete (incluye ${Math.max(
            0,
            Math.trunc(
              normalizeNumber(
                item.includedQuantity,
                quantity
              )
            )
          ).toLocaleString("es-MX")} muestras)`
        : `${quantity.toLocaleString("es-MX")} pz`;

      const productImage =
        getVisualDesignUrl(item) ||
        normalizeText(
          item.materialImageUrl
        );

      const materialImage =
        normalizeText(
          item.materialImageUrl
        );

      const localPreviewKey =
        getLocalDesignPreviewKey(item);

      const imageMarkup =
        productImage
          ? `
            <img
              class="peguu-cart-drawer__product-image"
              src="${escapeHtml(productImage)}"
              ${localPreviewKey
                ? `data-local-design-preview="${escapeHtml(localPreviewKey)}"`
                : ""}
              ${materialImage
                ? `data-material-image-fallback="${escapeHtml(materialImage)}"`
                : ""}
              alt="${escapeHtml(materialName)}"
              loading="lazy"
              width="86"
              height="86"
            >
          `
          : `
            <span
              class="peguu-cart-drawer__product-placeholder"
              aria-hidden="true"
            >
              P
            </span>
          `;

      const itemId =
        normalizeText(
          item.id
        );

      const source =
        item.source ===
        "customer"
          ? "customer"
          : "guest";

      const subtotal =
        normalizeNumber(
          item.lineTotal,
          0
        );

      const priceMarkup = formatMoney(subtotal);

      const trashMarkup =
        this.trashIconUrl
          ? `
            <img
              class="peguu-cart-drawer__remove-icon"
              src="${escapeHtml(
                this.trashIconUrl
              )}"
              alt=""
              aria-hidden="true"
              width="23"
              height="23"
            >
          `
          : `
            <span
              class="peguu-cart-drawer__remove-fallback"
              aria-hidden="true"
            >
              ×
            </span>
          `;

      const editMarkup =
        this.editIconUrl
          ? `
            <img
              class="peguu-cart-drawer__edit-icon"
              src="${escapeHtml(
                this.editIconUrl
              )}"
              alt=""
              aria-hidden="true"
              width="23"
              height="23"
            >
          `
          : `
            <span aria-hidden="true">✎</span>
          `;

      return `
        <article
          class="peguu-cart-drawer__product${isSamplePack ? " peguu-cart-drawer__product--sample-pack" : ""}"
          data-peguu-cart-item-id="${escapeHtml(
            itemId
          )}"
          data-peguu-cart-item-source="${escapeHtml(
            source
          )}"
        >
          <div class="peguu-cart-drawer__product-image-wrapper">
            ${imageMarkup}
          </div>

          <div class="peguu-cart-drawer__product-information">
            <h3 class="peguu-cart-drawer__product-name">
              ${escapeHtml(
                materialName
              )}
            </h3>

            <p class="peguu-cart-drawer__product-description">
              <span class="peguu-cart-drawer__product-quantity">
                ${escapeHtml(quantityText)}
              </span>
              ${shape
                ? `<span class="peguu-cart-drawer__product-shape">
                    ${escapeHtml(shape)}
                  </span>`
                : ""}
              <span class="peguu-cart-drawer__product-size">
                ${escapeHtml(size)}
              </span>
            </p>

            <p class="peguu-cart-drawer__product-price">
              ${priceMarkup}
            </p>

          </div>

          <div class="peguu-cart-drawer__product-actions">
            ${isSamplePack
              ? ""
              : `<button
                  class="peguu-cart-drawer__edit"
                  type="button"
                  aria-label="Editar ${escapeHtml(materialName)}"
                  title="Editar pedido"
                  data-peguu-cart-edit
                  data-item-id="${escapeHtml(itemId)}"
                >
                  ${editMarkup}
                </button>`}

            <button
              class="peguu-cart-drawer__remove"
              type="button"
              aria-label="Eliminar ${escapeHtml(
                materialName
              )} del carrito"
              data-peguu-cart-remove
              data-item-id="${escapeHtml(
                itemId
              )}"
              data-source="${escapeHtml(
                source
              )}"
            >
              ${trashMarkup}
            </button>
          </div>
        </article>
      `;
    }


    /* =========================
       CÁLCULOS
    ========================= */

    calculateStickerPieces(
      items
    ) {
      return items.reduce(
        (
          total,
          item
        ) => {
          const quantity =
            item.productCode ===
              "sample_pack"
              ? Math.max(
                  0,
                  Math.trunc(
                    normalizeNumber(
                      item.includedQuantity,
                      item.quantity
                    )
                  )
                )
              : Math.max(
                  0,
                  Math.trunc(
                    normalizeNumber(
                      item.quantity,
                      0
                    )
                  )
                );

          return (
            total +
            quantity
          );
        },
        0
      );
    }


    /* =========================
       ESTADO DEL CHECKOUT
    ========================= */

    updateCheckoutState(
      hasItems
    ) {
      if (
        !this.checkoutButton
      ) {
        return;
      }

      this.checkoutButton.classList.toggle(
        "is-disabled",
        !hasItems
      );

      this.checkoutButton.setAttribute(
        "aria-disabled",
        hasItems
          ? "false"
          : "true"
      );

      if (hasItems) {
        this.checkoutButton.removeAttribute(
          "tabindex"
        );
      } else {
        this.checkoutButton.setAttribute(
          "tabindex",
          "-1"
        );
      }
    }


    /* =========================
       CONTADORES DEL HEADER
    ========================= */

    updateGlobalCartCounters(
      itemCount
    ) {
      const count =
        Math.max(
          0,
          Math.trunc(
            normalizeNumber(
              itemCount,
              0
            )
          )
        );

      document
        .querySelectorAll(
          [
            "[data-peguu-cart-bubble]",
            "[data-cart-count]",
          ].join(",")
        )
        .forEach(
          (element) => {
            const displayCount =
              count > 99
                ? "+99"
                : String(count);

            element.textContent =
              displayCount;

            element.hidden =
              count <= 0;

            element.setAttribute(
              "aria-hidden",
              count <= 0
                ? "true"
                : "false"
            );
          }
        );

      document
        .querySelectorAll(
          "[data-peguu-cart-open]"
        )
        .forEach(
          (element) => {
            element.classList.toggle(
              "has-items",
              count > 0
            );
          }
        );
    }


    /* =========================
       ESTADOS
    ========================= */

    setLoading(
      isLoading,
      message =
        "Actualizando carrito..."
    ) {
      this.isLoading =
        isLoading;

      if (
        this.loadingElement
      ) {
        this.loadingElement.hidden =
          !isLoading;
      }

      if (
        this.loadingTextElement
      ) {
        this.loadingTextElement.textContent =
          message;
      }

      this.root.classList.toggle(
        "is-loading",
        isLoading
      );
    }


    showError(message) {
      if (
        !this.errorElement
      ) {
        return;
      }

      this.errorElement.textContent =
        message;

      this.errorElement.hidden =
        false;
    }


    clearError() {
      if (
        !this.errorElement
      ) {
        return;
      }

      this.errorElement.textContent =
        "";

      this.errorElement.hidden =
        true;
    }


    /* =========================
       ACCESIBILIDAD
    ========================= */

    trapFocus(event) {
      if (!this.panel) {
        return;
      }

      const focusableElements =
        this.panel.querySelectorAll(
          [
            "a[href]",
            "button:not([disabled])",
            "input:not([disabled])",
            "select:not([disabled])",
            "textarea:not([disabled])",
            '[tabindex]:not([tabindex="-1"])',
          ].join(",")
        );

      const elements =
        Array.from(
          focusableElements
        ).filter(
          (element) => {
            return (
              !element.hidden &&
              element.offsetParent !==
                null
            );
          }
        );

      if (!elements.length) {
        event.preventDefault();

        this.panel.focus();

        return;
      }

      const firstElement =
        elements[0];

      const lastElement =
        elements[
          elements.length - 1
        ];

      if (
        event.shiftKey &&
        document.activeElement ===
          firstElement
      ) {
        event.preventDefault();

        lastElement.focus();

        return;
      }

      if (
        !event.shiftKey &&
        document.activeElement ===
          lastElement
      ) {
        event.preventDefault();

        firstElement.focus();
      }
    }
  }


  /* =========================
     API PÚBLICA DEL CARRITO
  ========================= */

  window.PeguuCart = {
    getCurrentCart,

    async quoteItems(
      items,
      couponCodes = []
    ) {
      const quoteItems =
        Array.isArray(items)
          ? items
          : [];

      if (!quoteItems.length) {
        return null;
      }

      return quoteGuestCart(
        quoteItems,
        couponCodes
      );
    },

    async addSamplePack(material) {
      if (
        !material?.id ||
        material?.productCode !==
          "sample_pack"
      ) {
        throw new Error(
          "El paquete de muestras no está disponible."
        );
      }

      const token =
        getCustomerToken();

      if (token) {
        await addCustomerSamplePack(
          material,
          token
        );
      } else {
        addGuestSamplePack(material);
      }

      const cart =
        await this.refresh();

      document.dispatchEvent(
        new CustomEvent(
          "peguu:cart:updated",
          {
            detail: {
              source:
                token
                  ? "customer"
                  : "guest",
              action: "add",
              productCode:
                "sample_pack",
            },
          }
        )
      );

      return cart;
    },

    async startEdit(
      itemId,
      source,
      returnUrl
    ) {
      const cart =
        await getCurrentCart();
      const item =
        cart.items.find(
          (currentItem) =>
            normalizeText(
              currentItem?.id
            ) ===
              normalizeText(itemId) &&
            (
              !source ||
              currentItem?.source ===
                source
            )
        );

      if (!item) {
        throw new Error(
          "El producto que quieres editar ya no está en el carrito."
        );
      }

      beginCartItemEdit(
        item,
        returnUrl
      );
    },

    async updateItem(
      editState,
      item
    ) {
      const itemId =
        normalizeText(
          editState?.itemId
        );

      if (
        editState?.mode !== "edit" ||
        !itemId
      ) {
        throw new Error(
          "No encontramos el producto que estás editando."
        );
      }

      let cart;
      const updatedItem = {
        ...item,
        qrConfig:
          item.productCode ===
            "qr_sticker"
            ? {
                link:
                  normalizeText(
                    item.qrConfig?.link ||
                    item.qrConfig?.destinationUrl ||
                    item.qrConfig?.destination_url ||
                    item.qrConfig?.url
                  ),
                acabado:
                  normalizeText(
                    item.qrConfig?.acabado
                  ),
                posicion:
                  normalizeText(
                    item.qrConfig?.posicion
                  ),
              }
            : null,
      };

      if (
        updatedItem.productCode ===
          "qr_sticker" &&
        (
          !updatedItem.qrConfig.link ||
          !updatedItem.qrConfig.acabado ||
          !updatedItem.qrConfig.posicion
        )
      ) {
        throw new Error(
          "Completa la configuración QR antes de guardar."
        );
      }

      if (
        editState.cartType ===
        "authenticated"
      ) {
        const token =
          getCustomerToken();

        if (!token) {
          throw new Error(
            "Tu sesión expiró. Inicia sesión nuevamente para guardar los cambios."
          );
        }

        cart =
          await updateCustomerCartItem(
            itemId,
            updatedItem,
            token
          );
      } else {
        updateGuestItem(
          itemId,
          updatedItem
        );
        cart =
          await getCurrentCart();
      }

      if (window.peguuCartDrawer) {
        window.peguuCartDrawer.currentCart =
          cart;
        window.peguuCartDrawer.render(
          cart
        );
      }

      document.dispatchEvent(
        new CustomEvent(
          "peguu:cart:updated",
          {
            detail: {
              action: "edit",
              itemId,
              source:
                editState.cartType ===
                  "authenticated"
                  ? "customer"
                  : "guest",
              cart,
            },
          }
        )
      );

      return cart;
    },

    getGuestCart() {
      return readGuestCart();
    },

    getGuestCouponCode() {
      const appliedCode =
        getCartCouponCodes(
          window.peguuCartDrawer
            ?.currentCart
        )[0] || "";

      if (appliedCode) {
        return appliedCode;
      }

      if (
        window.peguuCartDrawer
          ?.currentCart &&
        !quotedCouponApplies(
          window.peguuCartDrawer
            .currentCart,
          readGuestCouponCode()
        )
      ) {
        return "";
      }

      return readGuestCouponCode();
    },

    getAppliedCouponCode() {
      return (
        getPromotionalCouponCodes(
          window.peguuCartDrawer
            ?.currentCart
        )[0] || ""
      );
    },

    getAppliedCouponCodes() {
      return getPromotionalCouponCodes(
        window.peguuCartDrawer
          ?.currentCart
      );
    },

    async getAvailableCoupons(
      cart
    ) {
      const currentCart =
        cart ||
        (await getCurrentCart());

      return fetchAvailableCoupons(
        currentCart
      );
    },

    async applyCoupon(code) {
      const normalizedCode =
        normalizeText(code)
          .toUpperCase();

      if (!normalizedCode) {
        throw new Error(
          "Ingresa un código de cupón."
        );
      }

      if (normalizedCode === AUTOMATIC_COUPON_CODE) {
        throw new Error(
          "El beneficio CLIENTEPEGUU se aplica automáticamente a clientes con correo verificado."
        );
      }

      const currentCart =
        window.peguuCartDrawer
          ?.currentCart ||
        await getCurrentCart();

      if (getPromotionalCouponCodes(currentCart).length) {
        throw new Error(
          "Solo puedes usar un cupón por pedido. Quita el actual para usar otro."
        );
      }

      const token =
        getCustomerToken();

      try {
        if (token) {
          const appliedCart =
            await applyCustomerCoupon(
              normalizedCode,
              token
            );

          rememberManualCustomerCoupon(
            normalizedCode,
            appliedCart
          );
        } else {
          saveGuestCouponCode(
            normalizedCode
          );
        }
      } catch (error) {
        if (error?.status === 401) {
          throw error;
        }

        const cart =
          await this.refresh();

        document.dispatchEvent(
          new CustomEvent(
            "peguu:cart:updated",
            {
              detail: {
                source:
                  token
                    ? "customer"
                    : "guest",
                action:
                  "coupon",
                cart,
              },
            }
          )
        );

        const apiError =
          new Error(
            error?.status === 429
              ? "Demasiados intentos con cupones. Espera un momento y vuelve a intentar."
              : error?.message ||
                "No fue posible aplicar el cupón."
          );

        apiError.status =
          error?.status || 400;

        throw apiError;
      }

      const cart =
        await this.refresh();

      document.dispatchEvent(
        new CustomEvent(
          "peguu:cart:updated",
          {
            detail: {
              source:
                token
                  ? "customer"
                  : "guest",
              action:
                "coupon",
              cart,
            },
          }
        )
      );

      if (!getCartCouponCodes(cart).includes(normalizedCode)) {
        if (!token) {
          removeGuestCouponCode(
            normalizedCode
          );
        }

        const apiError =
          new Error(
            cart?.couponError ||
            "No se encontró el cupón"
          );

        apiError.status = 400;

        throw apiError;
      }

      return cart;
    },

    async removeCoupon(code) {
      const token =
        getCustomerToken();

      const normalizedCode =
        normalizeText(code).toUpperCase();

      let cart;

      if (token) {
        const currentCart =
          window.peguuCartDrawer
            ?.currentCart ||
          await getCustomerCart(token);

        if (
          normalizedCode ===
            AUTOMATIC_COUPON_CODE
        ) {
          throw new Error(
            "Este cupón forma parte de tus beneficios y no se puede quitar."
          );
        }

        cart = await removeCustomerCoupon(
          token,
          normalizedCode
        );

        forgetManualCustomerCoupon(
          normalizedCode,
          currentCart
        );

        /*
         * Si el cupón llegó desde un carrito de invitado, también se elimina
         * de localStorage para que no vuelva a aplicarse automáticamente en
         * la siguiente sincronización de la cuenta.
         */
        removeGuestCouponCode(
          normalizedCode
        );

        if (window.peguuCartDrawer) {
          window.peguuCartDrawer
            .currentCart = cart;

          window.peguuCartDrawer
            .clearError();

          window.peguuCartDrawer
            .render(cart);
        }
      } else {
        removeGuestCouponCode(
          normalizedCode
        );

        cart = await this.refresh();
      }

      document.dispatchEvent(
        new CustomEvent(
          "peguu:cart:updated",
          {
            detail: {
              source:
                token
                  ? "customer"
                  : "guest",
              action:
                "coupon-remove",
              cart,
            },
          }
        )
      );

      return cart;
    },

    async clearGuestCart() {
      saveGuestCart([]);
      clearGuestCouponCode();

      return this.refresh();
    },

    async clearPaidCart() {
      const token =
        getCustomerToken();

      if (token) {
        const cart =
          await getCustomerCart(token);

        const couponCodes =
          getCartCouponCodes(cart).filter(
            (code) =>
              code !==
                AUTOMATIC_COUPON_CODE
          );

        for (const code of couponCodes) {
          await removeCustomerCoupon(
            token,
            code
          );

          forgetManualCustomerCoupon(
            code,
            cart
          );
        }

        const items =
          Array.isArray(cart?.items)
            ? cart.items
            : [];

        for (const item of items) {
          const itemId =
            getCartItemId(item);

          if (!itemId) {
            continue;
          }

          const response =
            await window.PeguuAuthClient.authFetch(
              `${API_BASE_URL}/me/cart/items/${encodeURIComponent(itemId)}`,
              {
                method: "DELETE",
                headers: {
                  Accept: "application/json",
                  Authorization:
                    `Bearer ${token}`,
                },
              }
            );

          await parseApiResponse(
            response,
            "No fue posible limpiar el carrito pagado."
          );

          removeCustomerItemMetadata(
            itemId
          );
        }
      }

      saveGuestCart([]);
      clearGuestCouponCode();
      window.localStorage.removeItem(
        CUSTOMER_CART_METADATA_KEY
      );
      window.localStorage.removeItem(
        DESIGN_FILE_METADATA_KEY
      );

      return this.refresh();
    },

    async mergeGuestCart() {
      await mergeGuestCartIntoCustomerCart();

      return getCurrentCart();
    },

    async preserveCustomerCartAsGuest() {
      return preserveCustomerCartAsGuest();
    },

    async refresh() {
      const cart =
        await getCurrentCart();

      if (window.peguuCartDrawer) {
        window.peguuCartDrawer
          .currentCart = cart;

        window.peguuCartDrawer
          .clearError();

        window.peguuCartDrawer
          .render(cart);
      }

      return cart;
    },

    open() {
      return (
        window.peguuCartDrawer
          ?.open() ||
        Promise.resolve()
      );
    },

    close() {
      window.peguuCartDrawer
        ?.close();
    },
  };

  document.dispatchEvent(
    new CustomEvent(
      "peguu:cart:ready",
      {
        detail: {
          cart:
            window.PeguuCart,
        },
      }
    )
  );


  /* =========================
     INICIALIZACIÓN
  ========================= */

  const initializePeguuCartDrawer =
    () => {
      const cartDrawer =
        document.querySelector(
          "[data-peguu-cart-drawer]"
        );

      if (!cartDrawer) {
        return;
      }

      if (
        cartDrawer.dataset
          .peguuInitialized ===
        "true"
      ) {
        return;
      }

      cartDrawer.dataset
        .peguuInitialized =
        "true";

      window.peguuCartDrawer =
        new PeguuCartDrawer(
          cartDrawer
        );
    };


  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      initializePeguuCartDrawer
    );
  } else {
    initializePeguuCartDrawer();
  }


  /* =========================
     SHOPIFY THEME EDITOR
  ========================= */

  document.addEventListener(
    "shopify:section:load",
    () => {
      initializePeguuCartDrawer();
    }
  );
})();
